class AAOperationsWorkspace {
  constructor(root) {
    this.root = root;
    this.month =
      this.getCurrentMonthValue();
    this.rows = [];
    this.clients = [];
    this.teamMembers = [];
    this.attention = {};
    this.waveConnection = null;
    this.signwellTest = { state: 'unchecked', message: '', templates: [] };
    this.signwellConfiguration = null;
    this.signwellSettingsOpen = false;
    this.signwellMappingBusy = false;
    this.signwellMappingMessage = '';
    this.signwellDraftOpen = false;
    this.signwellDraftBusy = false;
    this.signwellDraftMessage = '';
    this.signwellExhibitStatus = {state:'unchecked',message:''};
    this.signwellAddendumStatus = {state:'unchecked',message:''};
    this.signwellLastPreparedId = '';
    this.signwellLastPreparedCount = 0;
    this.signwellDrafts = [];
    this.signwellDraftTotal = 0;
    this.signwellDraftLoadError = '';
    this.signwellDraftsRefreshing = false;
    this.signwellBulkDeleteConfirm = false;
    this.signwellBulkDeleting = false;
    this.signwellBulkProgressMessage = '';
    this.signwellDraftRecoveryWarning = '';
    this.signwellDraftActionMessage = '';
    this.signwellConfirmDeleteId = '';
    this.signwellBusyId = '';
    this.signwellDraftFields = {
      templateType: 'service_agreement',
      packageChangeRequestId: '',
      clientId: '',
      clientName: '',
      clientEmail: '',
      owner1Name: 'Tiffany Moran',
      owner1Email: '',
      owner2Name: 'Whitney Kirkwood',
      owner2Email: '',
      packagePrice: '',
      managementFee: '0.00',
      oneTimeFee: '0.00',
      originalAgreementDate: '',
      addendumChange1: '',
      addendumChange2: '',
      addendumChange3: '',
      billingPeriod: 'per month',
      legalBusinessName: '',
      businessAddress: '',
      stateOfOrganization: '',
      effectiveDate: '',
      expirationDate: ''
    };
    this.signwellServiceCatalog = [];
    this.signwellDraftServices = [];
    this.signwellServicesBusy = false;
    this.signwellServicesMessage = '';
    this.signwellLibraryOpen = false;
    this.signwellLibraryEditId = '';
    this.signwellLibraryDeleteId = '';
    this.signwellLibraryMessage = '';
    this.signwellLibraryBusy = false;
    this.signwellLibraryValues = {
      name: '', category: 'Marketing', frequency: 'per month', description: ''
    };
    this.signwellPackageRequest = 0;
    this.signwellPreparationMode = 'test';
    this.signwellLiveDocuments = [];
    this.signwellLiveSendingEnabled = false;
    this.signwellLivePreparationEnabled = false;
    this.signwellLiveLoadError = '';
    this.signwellLiveInitialLoading = false;
    this.signwellLiveBusyId = '';
    this.signwellLiveMessage = '';
    this.signwellLiveRefreshing = false;
    this.signwellLiveRefreshNotice = '';
    this.signwellLiveCheckedAt = '';
    this.signwellReconciliation = null;
    this.signwellReconciliationStatusError = '';
    this.signwellLiveSendReview = null;
    this.signwellApprovedChanges = [];
    this.signwellLivePolling = false;
    this.signwellLivePollTimer = window.setInterval(() => {
      if (this.activeView === 'contracts' && !this.signwellLivePolling &&
          this.signwellLiveDocuments.some(doc =>
            ['sending','send_needs_reconciliation','sent','viewed',
             'partially_signed','fully_signed'].includes(doc.status))) {
        this.signwellLivePolling = true;
        // Polling is read-only. Unattended reconciliation is server-owned.
        this.refreshSignwellLiveAgreements(false).finally(() => {
          this.signwellLivePolling = false;
        });
      }
    }, 120000);
    this.loading = true;
    this.error = '';

    this.filterClientId = 'none';
    this.filterOwner = 'all';
    this.filterService = 'all';
    this.expandedClients =
      new Set();

    this.packageDeliveryOpen = false;

    this.activeView = 'overview';
    this.quotes = [];
    this.contracts = [];
    this.prospects = [];
    this.quoteServices = [];
    this.quoteBuilderOpen = false;
    this.quoteAudience = 'client';
    this.quoteType = 'monthly';
    this.quoteDraftItems = [];
    this.quoteMessage = '';
    this.quoteSaving = false;

    this.bindEvents();
    this.load();
  }

  escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  getCurrentMonthValue() {
    const date = new Date();

    return [
      date.getFullYear(),
      String(
        date.getMonth() + 1
      ).padStart(2, '0')
    ].join('-');
  }

  formatMonth(value) {
    const [year, month] =
      String(value || '')
        .split('-')
        .map(Number);

    if (!year || !month) {
      return value || '';
    }

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'long',
        year: 'numeric'
      }
    ).format(
      new Date(
        year,
        month - 1,
        1
      )
    );
  }

  isMonthlyTracked(row) {
    const unit =
      String(
        row?.unitLabel || ''
      ).toLowerCase();

    return !(
      unit.includes('year') ||
      unit.includes('quarter') ||
      unit === 'included'
    );
  }

  async load() {
    const generation = (this.loadGeneration || 0) + 1;
    this.loadGeneration = generation;
    this.loading = true;
    this.error = '';
    this.contractDataLoaded = false;
    this.contractDataLoading = false;
    this.contractDataError = '';
    // Invalidate any delayed SignWell draft response from the previous load.
    this.signwellDraftLoadGeneration = (this.signwellDraftLoadGeneration || 0) + 1;
    this.signwellDraftsRefreshing = false;
    this.render();

    try {
      // Only the essential overview requests block opening Operations.
      // SignWell and quote setup are loaded if and when Contracts & Quotes is opened.
      const [rows, clients, teamMembers, attention] = await Promise.all([
        window.AASupabaseClient.getOperationsPackageSummary({ month: this.month }),
        window.AASupabaseClient.getClients(),
        window.AASupabaseClient.getInternalTeamMembers(),
        window.AASupabaseClient.getOperationsAttention()
      ]);
      if (generation !== this.loadGeneration) return;

      this.rows = Array.isArray(rows) ? rows : [];
      this.clients = Array.isArray(clients)
        ? clients.filter(client => !['archived','inactive'].includes(
            String(client.account_status || client.active_status || '').toLowerCase()))
        : [];
      this.teamMembers = Array.isArray(teamMembers) ? teamMembers : [];
      this.attention = attention && typeof attention === 'object' ? attention : {};


      if (this.filterClientId === 'auto') {
        const tracked = this.rows.filter(row => this.isMonthlyTracked(row))
          .map(row => ({id:String(row.clientId || ''),name:row.clientName || ''}))
          .filter(item => item.id).sort((a,b) => String(a.name).localeCompare(String(b.name)));
        const active = this.clients.map(client => ({
          id:String(client.id || ''),name:client.business_name || ''
        })).filter(item => item.id).sort((a,b) => String(a.name).localeCompare(String(b.name)));
        this.filterClientId = tracked[0]?.id || active[0]?.id || 'all';
      }
    } catch (error) {
      if (generation !== this.loadGeneration) return;
      console.error('Unable to load Operations:', error);
      this.rows = [];
      this.attention = {};
      this.error = error?.message || 'Unable to load Operations.';
    } finally {
      if (generation === this.loadGeneration) {
        this.loading = false;
        this.render();
        if (!this.error) {
          this.loadWaveConnection(generation);
          if (this.activeView === 'contracts') this.loadContractData();
        }
      }
    }
  }

  async loadWaveConnection(generation) {
    if (!window.AAPlaybookService?.getWaveConnection) return;
    try {
      const waveResult = await window.AAPlaybookService.getWaveConnection();
      if (generation !== this.loadGeneration) return;
      this.waveConnection = waveResult?.success ? waveResult.connection || null : null;
      // Do not replace an open contract form while the user is editing it.
      if (!this.loading && this.activeView === 'overview') this.render();
    } catch (error) {
      // Wave connection status is optional on the Operations overview.
      if (generation === this.loadGeneration) this.waveConnection = null;
    }
  }

  async loadContractData() {
    if (this.contractDataLoading || this.contractDataLoaded || this.loading) return;
    const generation = this.loadGeneration;
    this.contractDataLoading = true;
    this.contractDataError = '';
    this.signwellLiveInitialLoading = true;
    this.render();
    try {
      // Only data needed to draw the Contracts & Quotes screen blocks opening.
      // The SignWell provider can be slow; its metadata must not block the page.
      const [quotes, contracts, prospects, quoteServices, signwellCatalog] = await Promise.all([
        window.AASupabaseClient.getQuotes(),
        window.AASupabaseClient.getContracts(),
        window.AASupabaseClient.getProspects(),
        window.AASupabaseClient.getQuoteServiceCatalog(),
        window.AAPlaybookService.getServiceCatalog().catch(() => [])
      ]);
      if (generation !== this.loadGeneration) return;
      this.quotes = Array.isArray(quotes) ? quotes : [];
      this.contracts = Array.isArray(contracts) ? contracts : [];
      this.prospects = Array.isArray(prospects) ? prospects : [];
      this.quoteServices = Array.isArray(quoteServices) ? quoteServices : [];
      this.signwellServiceCatalog = Array.isArray(signwellCatalog) ? signwellCatalog : [];
      this.contractDataLoaded = true;
      // Both data groups load independently. Neither can delay this page.
      void this.loadSignwellInitialDrafts(generation);
      void this.loadSignwellSupplementalData(generation);
    } catch (error) {
      if (generation !== this.loadGeneration) return;
      console.error('Unable to load contract details:', error);
      this.contractDataError = error?.message || 'Unable to load Contracts & Quotes.';
    } finally {
      if (generation === this.loadGeneration) {
        this.contractDataLoading = false;
        this.render();
      }
    }
  }

  async loadSignwellSupplementalData(generation) {
    const [configuration, signers, live, changes, progress] = await Promise.all([
      window.AASupabaseClient.getSignwellConfiguration().catch(() => null),
      window.AASupabaseClient.getSignwellDefaultSigners().catch(() => null),
      window.AASupabaseClient.getSignwellLiveAgreements().catch(error =>
        ({loadError:error?.message || 'Unable to retrieve live agreements.'})),
      window.AASupabaseClient.getApprovedAgreementPackageChanges().catch(() => []),
      window.AASupabaseClient.runSignwellLiveAction('reconciliation-status', null)
        .catch(error => ({loadError:error?.message || 'Reconciliation progress unavailable.'}))
    ]);
    if (generation !== this.loadGeneration) return;
    this.signwellConfiguration = {
      ...(configuration || {}),
      qaClientEmail:String(live?.qaClientEmail || '')
    };
    if (Array.isArray(live?.documents)) {
      this.signwellLiveDocuments = live.documents;
      this.signwellLivePreparationEnabled = live?.livePreparationEnabled === true;
      this.signwellLiveSendingEnabled = live?.sendingEnabled === true;
      this.signwellLiveCheckedAt = new Date().toISOString();
      this.signwellLiveLoadError = '';
    } else {
      // Never erase previously loaded agreements because a later load failed.
      this.signwellLiveLoadError = String(live?.loadError || 'Unable to load agreements.');
    }
    if (Array.isArray(progress?.streams)) {
      this.signwellReconciliation = progress;
      this.signwellReconciliationStatusError = '';
    } else {
      this.signwellReconciliationStatusError =
        String(progress?.loadError || 'Reconciliation progress unavailable.');
    }
    this.signwellApprovedChanges = Array.isArray(changes) ? changes : [];
    this.signwellLiveInitialLoading = false;
    const ownerSigners = signers?.signers || {};
    if (!this.signwellDraftFields.owner1Email && ownerSigners.owner1?.email)
      this.signwellDraftFields.owner1Email = ownerSigners.owner1.email;
    if (!this.signwellDraftFields.owner2Email && ownerSigners.owner2?.email)
      this.signwellDraftFields.owner2Email = ownerSigners.owner2.email;
    // Never replace a form while someone is entering signer names/dates.
    if (!this.signwellDraftOpen) this.render();
  }

  async loadSignwellInitialDrafts(generation) {
    if (this.signwellDraftsRefreshing) return;
    const requestId = (this.signwellDraftLoadGeneration || 0) + 1;
    this.signwellDraftLoadGeneration = requestId;
    this.signwellDraftsRefreshing = true;
    this.signwellDraftLoadError = '';
    this.render();
    try {
      const latest = await window.AASupabaseClient.getSignwellTestDrafts();
      if (generation !== this.loadGeneration ||
          requestId !== this.signwellDraftLoadGeneration) return;
      if (!Array.isArray(latest?.drafts)) {
        throw new Error(latest?.loadError ||
          'SignWell returned an incomplete unsigned agreement list.');
      }
      this.signwellDrafts = latest.drafts;
      this.signwellDraftTotal = Number.isFinite(Number(latest.totalTestDrafts))
        ? Number(latest.totalTestDrafts) : this.signwellDrafts.length;
      this.signwellDraftRecoveryWarning = String(latest.recoveryWarning || '');
      if (!this.signwellDrafts.length) this.signwellLastPreparedId = '';
    } catch (error) {
      if (generation !== this.loadGeneration ||
          requestId !== this.signwellDraftLoadGeneration) return;
      this.signwellDraftLoadError =
        error?.message || 'Unable to retrieve unsigned test drafts.';
      // Keep the previously loaded drafts if SignWell is temporarily unavailable.
    } finally {
      if (generation === this.loadGeneration &&
          requestId === this.signwellDraftLoadGeneration) {
        this.signwellDraftsRefreshing = false;
        if (!this.signwellDraftOpen) this.render();
      }
    }
  }

  getTrackedRows() {
    return this.rows.filter(
      (row) =>
        this.isMonthlyTracked(row)
    );
  }

  getFilteredRows() {
    let rows =
      this.getTrackedRows();

    if (
      this.filterClientId !== 'all'
    ) {
      rows = rows.filter(
        (row) =>
          String(row.clientId) ===
          String(
            this.filterClientId
          )
      );
    }

    if (
      this.filterOwner !== 'all'
    ) {
      rows = rows.filter(
        (row) =>
          String(row.ownerName) ===
          String(this.filterOwner)
      );
    }

    if (
      this.filterService !== 'all'
    ) {
      rows = rows.filter(
        (row) =>
          String(row.serviceName) ===
          String(
            this.filterService
          )
      );
    }

    return rows;
  }

  getTotals(rows) {
    return rows.reduce(
      (totals, row) => {
        totals.contracted +=
          Number(row.contracted || 0);

        totals.scheduled +=
          Number(row.scheduled || 0);

        totals.completed +=
          Number(row.completed || 0);

        totals.unscheduled +=
          Number(row.unscheduled || 0);

        totals.stillToComplete +=
          Number(
            row.stillToComplete || 0
          );

        return totals;
      },
      {
        contracted: 0,
        scheduled: 0,
        completed: 0,
        unscheduled: 0,
        stillToComplete: 0
      }
    );
  }

  getOwnerNameForClient(client) {
    const member =
      this.teamMembers.find(
        (item) =>
          String(item.id) ===
          String(
            client?.primary_team_member_id ||
            ''
          )
      );

    return (
      member?.display_name ||
      member?.aa_roles?.display_name ||
      'Unassigned'
    );
  }

  getClients(rows) {
    const grouped =
      new Map();

    this.clients.forEach(
      (client) => {
        grouped.set(
          String(client.id),
          {
            clientId:
              client.id,
            clientCode:
              client.client_code || '',
            clientName:
              client.business_name || '',
            ownerName:
              this.getOwnerNameForClient(
                client
              ),
            services: []
          }
        );
      }
    );

    rows.forEach((row) => {
      const key =
        String(row.clientId);

      if (!grouped.has(key)) {
        grouped.set(
          key,
          {
            clientId:
              row.clientId,
            clientCode:
              row.clientCode || '',
            clientName:
              row.clientName || '',
            ownerName:
              row.ownerName || '',
            services: []
          }
        );
      }

      const client =
        grouped.get(key);

      if (
        row.ownerName &&
        (
          !client.ownerName ||
          client.ownerName ===
            'Unassigned'
        )
      ) {
        client.ownerName =
          row.ownerName;
      }

      client.services.push(row);
    });

    let clients =
      [
        ...grouped.values()
      ].map((client) => ({
        ...client,
        totals:
          this.getTotals(
            client.services
          )
      }));

    if (
      this.filterClientId !== 'all'
    ) {
      clients =
        clients.filter(
          (client) =>
            String(client.clientId) ===
            String(
              this.filterClientId
            )
        );
    }

    if (
      this.filterOwner !== 'all'
    ) {
      clients =
        clients.filter(
          (client) =>
            String(client.ownerName) ===
            String(this.filterOwner)
        );
    }

    if (
      this.filterService !== 'all'
    ) {
      clients =
        clients.filter(
          (client) =>
            client.services.length > 0
        );
    }

    return clients.sort(
      (a, b) =>
        String(a.clientName)
          .localeCompare(
            String(b.clientName)
          )
    );
  }

  getClientOptions() {
    return this.clients
      .map(
        (client) => [
          String(client.id),
          client.business_name || ''
        ]
      )
      .sort(
        (a, b) =>
          String(a[1])
            .localeCompare(
              String(b[1])
            )
      );
  }

  getOwnerOptions() {
    return [
      ...new Set(
        this.clients
          .map(
            (client) =>
              this.getOwnerNameForClient(
                client
              )
          )
          .filter(Boolean)
      )
    ].sort();
  }

  getServiceOptions() {
    return [
      ...new Set(
        this.getTrackedRows()
          .map(
            (row) =>
              row.serviceName
          )
          .filter(Boolean)
      )
    ].sort();
  }

  renderSummaryCard(
    label,
    value,
    detail = ''
  ) {
    return `
      <div class="aa-operations-stat">
        <span>
          ${this.escapeHtml(label)}
        </span>
        <strong>
          ${this.escapeHtml(value)}
        </strong>
        ${detail ? `
          <small>
            ${this.escapeHtml(detail)}
          </small>
        ` : ''}
      </div>
    `;
  }

  renderFilters() {
    return `
      <div class="aa-operations-filters">
        <label>
          <span>Month</span>
          <input
            type="month"
            value="${this.escapeHtml(this.month)}"
            data-aa-operations-filter="month"
          >
        </label>

        <label>
          <span>Client</span>
          <select
            data-aa-operations-filter="client"
          >
            <option
              value="none"
              ${this.filterClientId === 'none' ? 'selected' : ''}
            >
              Select a client
            </option>

            <option
              value="all"
              ${this.filterClientId === 'all' ? 'selected' : ''}
            >
              All Clients
            </option>

            ${this.getClientOptions()
              .map(
                ([id, name]) => `
                  <option
                    value="${this.escapeHtml(id)}"
                    ${String(this.filterClientId) === String(id) ? 'selected' : ''}
                  >
                    ${this.escapeHtml(name)}
                  </option>
                `
              )
              .join('')}
          </select>
        </label>

        <label>
          <span>Owner</span>
          <select
            data-aa-operations-filter="owner"
          >
            <option value="all">
              Everyone
            </option>

            ${this.getOwnerOptions()
              .map(
                (name) => `
                  <option
                    value="${this.escapeHtml(name)}"
                    ${String(this.filterOwner) === String(name) ? 'selected' : ''}
                  >
                    ${this.escapeHtml(name)}
                  </option>
                `
              )
              .join('')}
          </select>
        </label>

        <label>
          <span>Service</span>
          <select
            data-aa-operations-filter="service"
          >
            <option value="all">
              All Services
            </option>

            ${this.getServiceOptions()
              .map(
                (name) => `
                  <option
                    value="${this.escapeHtml(name)}"
                    ${String(this.filterService) === String(name) ? 'selected' : ''}
                  >
                    ${this.escapeHtml(name)}
                  </option>
                `
              )
              .join('')}
          </select>
        </label>
      </div>
    `;
  }

  getClientAccountHref(
    clientId,
    section = ''
  ) {
    if (!clientId) {
      return '/pages/artisans-avenue-clients';
    }

    const planningSections =
      new Set([
        'package',
        'rules',
        'anchors',
        'playbooks',
        'reminders'
      ]);

    const params =
      new URLSearchParams({
        aa_client:
          String(clientId),
        aa_tab:
          planningSections.has(section)
            ? 'planning'
            : 'account'
      });

    if (section) {
      params.set(
        'aa_section',
        section
      );
    }

    return `/pages/artisans-avenue-clients?${params.toString()}`;
  }

  getTaskHref(taskId) {
    if (!taskId) {
      return '/pages/artisans-avenue-my-work';
    }

    const params =
      new URLSearchParams({
        aa_task:
          String(taskId)
      });

    return `/pages/artisans-avenue-my-work?${params.toString()}`;
  }

  formatAttentionStatus(value) {
    return String(value || '')
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (letter) =>
        letter.toUpperCase()
      );
  }

  renderOverview() {
    const cards = [
      {
        label: 'Package Requests',
        value:
          Number(
            this.attention
              ?.packageRequestCount || 0
          ),
        detail:
          'Waiting for review',
        target:
          'package-requests'
      },
      {
        label: 'Access Needed',
        value:
          Number(
            this.attention
              ?.accessIssueCount || 0
          ),
        detail:
          'Required access unresolved',
        target:
          'access-issues'
      },
      {
        label: 'Agreements',
        value:
          Number(
            this.attention
              ?.contractAttentionCount || 0
          ),
        detail:
          'Need attention',
        target:
          'billing-agreements'
      },
      {
        label: 'Billing Setup',
        value:
          Number(
            this.attention
              ?.billingUnlinked || 0
          ),
        detail:
          'Clients not linked to Wave',
        target:
          'billing-agreements'
      }
    ];

    return `
      <section class="aa-operations-overview">
        <div class="aa-operations-section__heading">
          <div>
            <p class="aa-card__label">
              Operations Overview
            </p>
            <h2>
              What needs management attention
            </h2>
          </div>
        </div>

        <div class="aa-operations-overview__grid">
          ${cards.map(
            (card) => `
              <button
                type="button"
                class="aa-operations-overview__card
                  ${card.value > 0 ? 'needs-attention' : ''}"
                data-aa-operations-jump="${this.escapeHtml(
                  card.target
                )}"
              >
                <span>
                  ${this.escapeHtml(
                    card.label
                  )}
                </span>

                <strong>
                  ${card.value}
                </strong>

                <small>
                  ${this.escapeHtml(
                    card.detail
                  )}
                </small>
              </button>
            `
          ).join('')}
        </div>
      </section>
    `;
  }

  renderAgreementRows() {
    const followUps =
      Array.isArray(
        this.attention
          ?.contractAttention
      )
        ? this.attention
            .contractAttention
        : [];

    const missing =
      Array.isArray(
        this.attention
          ?.missingContracts
      )
        ? this.attention
            .missingContracts
        : [];

    const rows = [
      ...followUps.map(
        (item) => ({
          clientId:
            item.clientId || '',
          clientName:
            item.clientName || '',
          detail:
            item.title ||
            'Client agreement',
          status:
            this.formatAttentionStatus(
              item.status
            )
        })
      ),
      ...missing.map(
        (item) => ({
          clientName:
            item.clientName || '',
          detail:
            'No current agreement on file',
          status:
            'Missing'
        })
      )
    ];

    if (!rows.length) {
      return `
        <div class="aa-operations-mini-empty">
          No agreement follow-up is needed.
        </div>
      `;
    }

    const visible =
      rows.slice(0, 3);

    return `
      <div class="aa-operations-mini-list">
        ${visible.map(
          (item) => `
            <a
              class="aa-operations-mini-row aa-operations-mini-row--link"
              href="${this.escapeHtml(
                this.getClientAccountHref(
                  item.clientId,
                  'agreements'
                )
              )}"
            >
              <div>
                <strong>
                  ${this.escapeHtml(
                    item.clientName
                  )}
                </strong>
                <span>
                  ${this.escapeHtml(
                    item.detail
                  )}
                </span>
              </div>
              <b>
                ${this.escapeHtml(
                  item.status
                )}
              </b>
            </a>
          `
        ).join('')}

        ${rows.length > visible.length ? `
          <div class="aa-operations-mini-more">
            +${rows.length - visible.length}
            more agreement item${rows.length - visible.length === 1 ? '' : 's'}
          </div>
        ` : ''}
      </div>
    `;
  }

  renderBillingAgreements() {
    const clientCount =
      Number(
        this.attention
          ?.clientCount ||
        this.clients.length ||
        0
      );

    const linked =
      Number(
        this.attention
          ?.billingLinked || 0
      );

    const unlinked =
      Number(
        this.attention
          ?.billingUnlinked || 0
      );

    const unlinkedClients =
      Array.isArray(
        this.attention
          ?.unlinkedClients
      )
        ? this.attention
            .unlinkedClients
        : [];

    return `
      <section
        class="aa-operations-section"
        data-aa-operations-section="billing-agreements"
      >
        <div class="aa-operations-section__heading">
          <div>
            <p class="aa-card__label">
              Billing & Agreements
            </p>
            <h2>
              Account administration
            </h2>
          </div>
        </div>

        <div class="aa-operations-management-grid">
          <article class="aa-operations-management-card">
            <div class="aa-operations-management-card__top">
              <div>
                <span>Wave Billing</span>
                <strong>
                  ${linked}/${clientCount}
                  clients linked
                </strong>
              </div>

              <b class="${unlinked > 0 ? 'needs-attention' : ''}">
                ${unlinked}
                need setup
              </b>
            </div>

            <p>
              ${this.waveConnection?.businessName
                ? `Connected to ${this.escapeHtml(
                    this.waveConnection.businessName
                  )}.`
                : 'Wave is not connected.'
              }
              Invoice balances and overdue status will roll up here from the client billing links.
            </p>

            ${unlinkedClients.length ? `
              <div class="aa-operations-mini-list">
                ${unlinkedClients
                  .slice(0, 3)
                  .map(
                    (item) => `
                      <a
                        class="aa-operations-mini-row aa-operations-mini-row--link"
                        href="${this.escapeHtml(
                          this.getClientAccountHref(
                            item.clientId,
                            'agreements'
                          )
                        )}"
                      >
                        <div>
                          <strong>
                            ${this.escapeHtml(
                              item.clientName
                            )}
                          </strong>
                          <span>
                            Connect Wave billing
                          </span>
                        </div>
                        <b>Set Up</b>
                      </a>
                    `
                  )
                  .join('')}
              </div>
            ` : ''}
          </article>

          <article class="aa-operations-management-card">
            <div class="aa-operations-management-card__top">
              <div>
                <span>Agreements</span>
                <strong>
                  ${Number(
                    this.attention
                      ?.contractAttentionCount ||
                    0
                  )}
                  need attention
                </strong>
              </div>
            </div>

            ${this.renderAgreementRows()}
          </article>
        </div>
      </section>
    `;
  }

  renderExceptionGroup({
    title,
    count,
    items,
    type
  }) {
    const safeItems =
      Array.isArray(items)
        ? items
        : [];

    return `
      <article
        class="aa-operations-exception-group"
        data-aa-operations-section="${this.escapeHtml(
          type === 'package'
            ? 'package-requests'
            : type === 'access'
              ? 'access-issues'
              : 'blocked-work'
        )}"
      >
        <div class="aa-operations-exception-group__header">
          <div>
            <span>
              ${this.escapeHtml(title)}
            </span>
            <strong>
              ${count}
            </strong>
          </div>
        </div>

        ${safeItems.length
          ? `
            <div class="aa-operations-mini-list">
              ${safeItems
                .slice(0, 3)
                .map((item) => {
                  let detail = '';
                  let status = '';

                  if (type === 'package') {
                    detail =
                      [
                        item.serviceName,
                        item.currentValue &&
                        item.requestedValue
                          ? `${item.currentValue} → ${item.requestedValue}`
                          : ''
                      ]
                        .filter(Boolean)
                        .join(' · ');

                    status =
                      this.formatAttentionStatus(
                        item.status
                      );
                  }

                  if (type === 'access') {
                    detail =
                      item.platformName ||
                      'Required access';

                    status =
                      this.formatAttentionStatus(
                        item.status
                      );
                  }

                  if (type === 'blocked') {
                    detail =
                      [
                        item.taskType,
                        item.specificItem
                      ]
                        .filter(Boolean)
                        .join(' · ');

                    status =
                      this.formatAttentionStatus(
                        item.status
                      );
                  }

                  return `
                    <a
                      class="aa-operations-mini-row aa-operations-mini-row--link"
                      href="${this.escapeHtml(
                        type === 'blocked'
                          ? this.getTaskHref(
                              item.id
                            )
                          : this.getClientAccountHref(
                              item.clientId,
                              type === 'package'
                                ? 'package'
                                : 'access'
                            )
                      )}"
                    >
                      <div>
                        <strong>
                          ${this.escapeHtml(
                            item.clientName ||
                            'Internal'
                          )}
                        </strong>
                        <span>
                          ${this.escapeHtml(
                            detail
                          )}
                        </span>
                      </div>
                      <b>
                        ${this.escapeHtml(
                          status
                        )}
                      </b>
                    </a>
                  `;
                })
                .join('')}

              ${safeItems.length > 3 ? `
                <div class="aa-operations-mini-more">
                  +${safeItems.length - 3}
                  more
                </div>
              ` : ''}
            </div>
          `
          : `
            <div class="aa-operations-mini-empty">
              Nothing needs attention.
            </div>
          `
        }
      </article>
    `;
  }

  renderExceptions() {
    const packageRequests =
      Array.isArray(
        this.attention
          ?.packageRequests
      )
        ? this.attention
            .packageRequests
        : [];

    const accessIssues =
      Array.isArray(
        this.attention
          ?.accessIssues
      )
        ? this.attention
            .accessIssues
        : [];

    const blockedTasks =
      Array.isArray(
        this.attention
          ?.blockedTasks
      )
        ? this.attention
            .blockedTasks
        : [];

    return `
      <section class="aa-operations-section">
        <div class="aa-operations-section__heading">
          <div>
            <p class="aa-card__label">
              Operational Exceptions
            </p>
            <h2>
              Things that require intervention
            </h2>
          </div>
        </div>

        <div class="aa-operations-exception-grid">
          ${this.renderExceptionGroup({
            title:
              'Package Change Requests',
            count:
              Number(
                this.attention
                  ?.packageRequestCount ||
                0
              ),
            items:
              packageRequests,
            type:
              'package'
          })}

          ${this.renderExceptionGroup({
            title:
              'Missing / Unverified Access',
            count:
              Number(
                this.attention
                  ?.accessIssueCount ||
                0
              ),
            items:
              accessIssues,
            type:
              'access'
          })}

          ${this.renderExceptionGroup({
            title:
              'Blocked Work',
            count:
              Number(
                this.attention
                  ?.blockedTaskCount ||
                0
              ),
            items:
              blockedTasks,
            type:
              'blocked'
          })}
        </div>
      </section>
    `;
  }

  renderClient(client) {
    const totals =
      client.totals;

    const over =
      Math.max(
        totals.scheduled -
        totals.contracted,
        0
      );

    const under =
      Math.max(
        totals.contracted -
        totals.scheduled,
        0
      );

    const expanded =
      this.filterClientId !== 'all' ||
      this.expandedClients.has(
        String(client.clientId)
      );

    return `
      <section
        class="aa-operations-client
          ${over > 0 ? 'is-over' : ''}"
      >
        <button
          type="button"
          class="aa-operations-client__summary"
          data-aa-operations-client-toggle="${this.escapeHtml(
            client.clientId
          )}"
          aria-expanded="${expanded}"
        >
          <div class="aa-operations-client__identity">
            <span>
              ${this.escapeHtml(
                client.clientCode ||
                'CLIENT'
              )}
            </span>

            <strong>
              ${this.escapeHtml(
                client.clientName
              )}
            </strong>

            <small>
              ${this.escapeHtml(
                client.ownerName ||
                'Unassigned'
              )}
            </small>
          </div>

          <div>
            <span>Contracted</span>
            <strong>
              ${totals.contracted}
            </strong>
          </div>

          <div>
            <span>Scheduled</span>
            <strong>
              ${totals.scheduled}
            </strong>
          </div>

          <div>
            <span>Completed</span>
            <strong>
              ${totals.completed}
            </strong>
          </div>

          <div>
            <span>Unscheduled</span>
            <strong>
              ${totals.unscheduled}
            </strong>
          </div>

          <div>
            <span>Still Open</span>
            <strong>
              ${totals.stillToComplete}
            </strong>
          </div>

          <div
            class="aa-operations-client__variance
              ${over > 0 ? 'is-negative' : ''}"
          >
            <span>
              ${over > 0
                ? 'Over'
                : 'Under'
              }
            </span>

            <strong>
              ${over > 0
                ? over
                : under
              }
            </strong>
          </div>

          <span class="aa-operations-client__chevron">
            ${expanded ? '−' : '+'}
          </span>
        </button>

        ${expanded ? `
          <div class="aa-operations-service-list">
            ${client.services.length
              ? client.services
              .map((service) => {
                const serviceOver =
                  Math.max(
                    Number(service.scheduled) -
                    Number(service.contracted),
                    0
                  );

                return `
                  <div
                    class="aa-operations-service
                      ${serviceOver > 0 ? 'is-over' : ''}"
                  >
                    <strong>
                      ${this.escapeHtml(
                        service.serviceName
                      )}
                    </strong>

                    <span>
                      ${service.contracted}
                      contracted
                    </span>

                    <span>
                      ${service.scheduled}
                      scheduled
                    </span>

                    <span>
                      ${service.completed}
                      completed
                    </span>

                    <span>
                      ${service.unscheduled}
                      unscheduled
                    </span>

                    ${serviceOver > 0 ? `
                      <b>
                        ${serviceOver} over
                      </b>
                    ` : '<b></b>'}
                  </div>
                `;
              })
              .join('')
              : `
                <div class="aa-operations-service aa-operations-service--empty">
                  <strong>
                    Package not configured
                  </strong>
                  <span>
                    Set this client's recurring deliverables in Clients.
                  </span>
                </div>
              `
            }
          </div>
        ` : ''}
      </section>
    `;
  }

  renderBusinessHealth() {
    const totals =
      this.getTotals(
        this.getTrackedRows()
      );

    const completion =
      totals.contracted > 0
        ? Math.round(
            (
              totals.completed /
              totals.contracted
            ) * 100
          )
        : 0;

    const clientCount =
      Number(
        this.attention?.clientCount ||
        this.clients.length ||
        0
      );

    const linked =
      Number(
        this.attention?.billingLinked ||
        0
      );

    const unlinked =
      Number(
        this.attention?.billingUnlinked ||
        0
      );

    const selectedDeliveryClient =
      this.clients.find(
        (client) =>
          String(client.id) ===
          String(this.filterClientId)
      );

    const deliveryLabel =
      selectedDeliveryClient
        ?.business_name ||
      (
        this.filterClientId === 'all'
          ? 'All Clients'
          : 'Choose a client'
      );

    return `
      <div class="aa-operations-command-column">
        <p class="aa-operations-column-label">
          Business Health
        </p>

        <article class="aa-operations-command-card">
          <div class="aa-operations-command-card__header">
            <div>
              <p class="aa-card__label">
                Monthly Delivery
              </p>
              <h2>
                ${this.escapeHtml(
                  this.formatMonth(
                    this.month
                  )
                )}
              </h2>
            </div>

            <span class="aa-operations-completion-pill">
              ${this.escapeHtml(completion)}% complete
            </span>
          </div>

          <div class="aa-operations-health-grid">
            ${this.renderSummaryCard(
              'Contracted',
              totals.contracted
            )}

            ${this.renderSummaryCard(
              'Scheduled',
              totals.scheduled
            )}

            ${this.renderSummaryCard(
              'Completed',
              totals.completed
            )}

            ${this.renderSummaryCard(
              'Unscheduled',
              totals.unscheduled
            )}

            ${this.renderSummaryCard(
              'Still Open',
              totals.stillToComplete
            )}
          </div>
        </article>

        <article class="aa-operations-command-card aa-operations-command-card--billing">
          <div class="aa-operations-command-card__header">
            <div>
              <p class="aa-card__label">
                Billing
              </p>

              <h2>
                Wave connection
              </h2>
            </div>

            <span
              class="aa-operations-completion-pill
                ${unlinked > 0 ? 'needs-attention' : ''}"
            >
              ${this.escapeHtml(linked)}/${this.escapeHtml(clientCount)} linked
            </span>
          </div>

          <p class="aa-operations-command-copy">
            ${this.waveConnection?.businessName
              ? `Connected to ${this.escapeHtml(
                  this.waveConnection.businessName
                )}.`
              : 'Wave is not connected.'
            }
          </p>

          ${Array.isArray(this.attention?.unlinkedClients) &&
            this.attention.unlinkedClients.length
              ? `
                <div class="aa-operations-billing-needs">
                  <span>Needs billing setup</span>

                  <div>
                    ${this.attention.unlinkedClients
                      .slice(0, 4)
                      .map((item) => `
                        <a
                          class="aa-operations-billing-client"
                          href="${this.escapeHtml(
                            this.getClientAccountHref(
                              item.clientId,
                              'agreements'
                            )
                          )}"
                        >
                          <strong>
                            ${this.escapeHtml(
                              item.clientName ||
                              'Client'
                            )}
                          </strong>
                          <span>Open setup</span>
                        </a>
                      `)
                      .join('')}
                  </div>
                </div>
              `
              : ''
          }
        </article>


      </div>
    `;
  }

  renderAgreementExceptionGroup() {
    const count =
      Number(
        this.attention
          ?.contractAttentionCount ||
        0
      );

    return `
      <article
        class="aa-operations-exception-group"
        data-aa-operations-section="billing-agreements"
      >
        <div class="aa-operations-exception-group__header">
          <div>
            <span>
              Agreement Issues
            </span>
            <strong>
              ${this.escapeHtml(count)}
            </strong>
          </div>
        </div>

        ${this.renderAgreementRows()}
      </article>
    `;
  }

  renderInterventionCenter() {
    const packageRequests =
      Array.isArray(
        this.attention
          ?.packageRequests
      )
        ? this.attention.packageRequests
        : [];

    const accessIssues =
      Array.isArray(
        this.attention
          ?.accessIssues
      )
        ? this.attention.accessIssues
        : [];

    const blockedTasks =
      Array.isArray(
        this.attention
          ?.blockedTasks
      )
        ? this.attention.blockedTasks
        : [];

    return `
      <div class="aa-operations-command-column">
        <p class="aa-operations-column-label">
          Requires Intervention
        </p>

        <div class="aa-operations-intervention-stack">
          ${this.renderExceptionGroup({
            title:
              'Package Change Requests',
            count:
              Number(
                this.attention
                  ?.packageRequestCount ||
                0
              ),
            items:
              packageRequests,
            type:
              'package'
          })}

          ${this.renderExceptionGroup({
            title:
              'Missing / Unverified Access',
            count:
              Number(
                this.attention
                  ?.accessIssueCount ||
                0
              ),
            items:
              accessIssues,
            type:
              'access'
          })}

          ${this.renderAgreementExceptionGroup()}

          ${this.renderExceptionGroup({
            title:
              'Blocked Work',
            count:
              Number(
                this.attention
                  ?.blockedTaskCount ||
                0
              ),
            items:
              blockedTasks,
            type:
              'blocked'
          })}
        </div>
      </div>
    `;
  }

  renderPackageDelivery() {
    const filteredRows =
      this.getFilteredRows();

    const totals =
      this.getTotals(
        filteredRows
      );

    const clients =
      this.getClients(
        filteredRows
      );

    const over =
      Math.max(
        totals.scheduled -
        totals.contracted,
        0
      );

    return `
      <section class="aa-operations-package aa-operations-package--nested">
        <p class="aa-operations-package__intro">
          Choose a client to review contracted, scheduled, completed,
          and remaining work. All Clients is available when you need
          the full rollup.
        </p>

        ${this.renderFilters()}

        <div class="aa-operations-client-list">
          ${this.filterClientId === 'none'
            ? `
              <div class="aa-operations-state">
                Select a client to view package delivery.
              </div>
            `
            : clients.length
              ? clients
                  .map(
                    (client) =>
                      this.renderClient(
                        client
                      )
                  )
                  .join('')
              : `
                <div class="aa-operations-state">
                  No package delivery records match these filters.
                </div>
              `
          }
        </div>

        <p class="aa-operations-package__note">
          Weekly services use four planning weeks per month.
          Annual, quarterly, and included-only package items
          are excluded from monthly production totals.
        </p>
      </section>
    `;
  }


  formatCurrency(value) {
    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency: 'USD'
      }
    ).format(
      Number(value || 0)
    );
  }

  formatDate(value) {
    if (!value) {
      return '';
    }

    const date =
      new Date(
        String(value).length === 10
          ? `${value}T12:00:00`
          : value
      );

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return value;
    }

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }
    ).format(date);
  }

  getDefaultValidThrough() {
    const date = new Date();

    date.setDate(
      date.getDate() + 30
    );

    return date
      .toISOString()
      .slice(0, 10);
  }

  getClientName(clientId) {
    return (
      this.clients.find(
        (client) =>
          String(client.id) ===
          String(clientId || '')
      )?.business_name ||
      'Client'
    );
  }

  getProspectName(prospectId) {
    return (
      this.prospects.find(
        (prospect) =>
          String(prospect.id) ===
          String(prospectId || '')
      )?.business_name ||
      'Prospect'
    );
  }

  getQuotePartyName(quote) {
    if (
      quote?.audience_type ===
        'client'
    ) {
      return this.getClientName(
        quote.client_id
      );
    }

    return this.getProspectName(
      quote?.prospect_id
    );
  }

  getQuoteTotal(quote) {
    const monthly =
      Number(
        quote?.monthly_subtotal ||
        0
      );

    const oneTime =
      Number(
        quote?.one_time_total ||
        0
      );

    if (
      monthly > 0 &&
      oneTime > 0
    ) {
      return `${this.formatCurrency(
        oneTime
      )} + ${this.formatCurrency(
        monthly
      )}/mo`;
    }

    if (monthly > 0) {
      return `${this.formatCurrency(
        monthly
      )}/mo`;
    }

    return this.formatCurrency(
      oneTime
    );
  }

  makeQuoteItem(service) {
    const source =
      service || null;

    return {
      serviceCatalogId:
        source?.id || '',
      serviceName:
        source?.name || '',
      description:
        source?.description || '',
      scopeText:
        source?.scope || '',
      frequency:
        source?.frequency || '',
      pricingType:
        source?.pricingType ||
        (
          this.quoteType === 'project'
            ? 'one_time'
            : this.quoteType === 'monthly'
              ? 'monthly'
              : 'custom'
        ),
      quantity: 1,
      unitPrice:
        source?.defaultPrice ?? 0
    };
  }

  syncQuoteItemsFromDom() {
    const rows =
      this.root.querySelectorAll(
        '[data-aa-quote-item]'
      );

    if (!rows.length) {
      return;
    }

    this.quoteDraftItems =
      [...rows].map(
        (row) => {
          const value = (field) =>
            row.querySelector(
              `[data-aa-quote-item-field="${field}"]`
            )?.value ?? '';

          return {
            serviceCatalogId:
              value('serviceCatalogId'),
            serviceName:
              value('serviceName'),
            description:
              value('description'),
            scopeText:
              value('scopeText'),
            frequency:
              value('frequency'),
            pricingType:
              value('pricingType') ||
              'custom',
            quantity:
              Number(
                value('quantity') ||
                1
              ),
            unitPrice:
              Number(
                value('unitPrice') ||
                0
              )
          };
        }
      );
  }

  renderOperationsTabs() {
    const tabs = [
      ['overview', 'Overview'],
      [
        'contracts',
        'Contracts & Quotes'
      ],
      [
        'packages',
        'Monthly Client Packages'
      ]
    ];

    return `
      <nav
        class="aa-operations-local-nav"
        aria-label="Operations sections"
      >
        ${tabs.map(
          ([value, label]) => `
            <button
              type="button"
              class="${this.activeView === value ? 'is-active' : ''}"
              data-aa-operations-view="${value}"
            >
              ${this.escapeHtml(label)}
            </button>
          `
        ).join('')}
      </nav>
    `;
  }

  renderQuoteBuilder() {
    const clientOptions =
      this.clients
        .slice()
        .sort(
          (a, b) =>
            String(
              a.business_name || ''
            ).localeCompare(
              String(
                b.business_name || ''
              )
            )
        );

    const senderOptions =
      this.teamMembers
        .filter(
          (member) =>
            member.is_active !== false
        )
        .slice()
        .sort(
          (a, b) =>
            String(
              a.display_name || ''
            ).localeCompare(
              String(
                b.display_name || ''
              )
            )
        );

    return `
      <form
        class="aa-quote-builder"
        data-aa-quote-builder
      >
        <div class="aa-quote-builder__header">
          <div>
            <p class="aa-card__label">
              New Quote
            </p>
            <h2>
              Build inside Headquarters
            </h2>
            <p>
              Choose the client or prospect, add services,
              set pricing, and save the quote as a draft.
            </p>
          </div>

          <button
            type="button"
            class="aa-button"
            data-aa-quote-close
          >
            Close
          </button>
        </div>

        <div class="aa-quote-builder__grid">
          <label>
            <span>Who is this for?</span>
            <select
              name="audienceType"
              data-aa-quote-audience
            >
              <option
                value="client"
                ${this.quoteAudience === 'client' ? 'selected' : ''}
              >
                Existing Client
              </option>
              <option
                value="prospect"
                ${this.quoteAudience === 'prospect' ? 'selected' : ''}
              >
                New Prospect
              </option>
            </select>
          </label>

          ${this.quoteAudience === 'client'
            ? `
              <label>
                <span>Client</span>
                <select
                  name="clientId"
                  required
                >
                  <option value="">
                    Select client
                  </option>
                  ${clientOptions.map(
                    (client) => `
                      <option
                        value="${this.escapeHtml(client.id)}"
                      >
                        ${this.escapeHtml(
                          client.business_name ||
                          'Client'
                        )}
                      </option>
                    `
                  ).join('')}
                </select>
              </label>
            `
            : `
              <label>
                <span>Business</span>
                <input
                  type="text"
                  name="prospectBusiness"
                  required
                  placeholder="Business name"
                >
              </label>

              <label>
                <span>Contact</span>
                <input
                  type="text"
                  name="recipientName"
                  placeholder="Contact name"
                >
              </label>

              <label>
                <span>Email</span>
                <input
                  type="email"
                  name="recipientEmail"
                  placeholder="name@business.com"
                >
              </label>

              <label>
                <span>Phone</span>
                <input
                  type="tel"
                  name="recipientPhone"
                  placeholder="Optional"
                >
              </label>
            `
          }

          <label>
            <span>Quote Type</span>
            <select
              name="quoteType"
              data-aa-quote-type
            >
              <option
                value="monthly"
                ${this.quoteType === 'monthly' ? 'selected' : ''}
              >
                Monthly Services
              </option>
              <option
                value="project"
                ${this.quoteType === 'project' ? 'selected' : ''}
              >
                One-Time Project
              </option>
              <option
                value="custom"
                ${this.quoteType === 'custom' ? 'selected' : ''}
              >
                Custom / Mixed
              </option>
            </select>
          </label>

          <label>
            <span>Send From</span>
            <select
              name="senderTeamMemberId"
            >
              <option value="">
                Choose sender
              </option>
              ${senderOptions.map(
                (member) => `
                  <option
                    value="${this.escapeHtml(member.id)}"
                  >
                    ${this.escapeHtml(
                      member.display_name ||
                      'Team Member'
                    )}
                  </option>
                `
              ).join('')}
            </select>
          </label>

          <label>
            <span>Title</span>
            <input
              type="text"
              name="title"
              value="Artisan's Avenue Quote"
            >
          </label>

          <label>
            <span>Valid Through</span>
            <input
              type="date"
              name="validThrough"
              value="${this.getDefaultValidThrough()}"
            >
          </label>

          ${this.quoteAudience === 'client'
            ? `
              <label>
                <span>Recipient Name</span>
                <input
                  type="text"
                  name="recipientName"
                  placeholder="Who should receive it?"
                >
              </label>

              <label>
                <span>Recipient Email</span>
                <input
                  type="email"
                  name="recipientEmail"
                  placeholder="name@business.com"
                >
              </label>
            `
            : ''
          }

          <label class="aa-quote-builder__wide">
            <span>Intro / Scope Note</span>
            <textarea
              name="introText"
              rows="3"
              placeholder="Optional short introduction for this quote"
            ></textarea>
          </label>
        </div>

        <div class="aa-quote-builder__services">
          <div class="aa-quote-builder__section-heading">
            <div>
              <p class="aa-card__label">
                Services
              </p>
              <h3>
                What are we quoting?
              </h3>
            </div>

            <button
              type="button"
              class="aa-button"
              data-aa-quote-add-service
            >
              + Add Another Service
            </button>
          </div>

          ${this.quoteDraftItems.length
            ? this.quoteDraftItems
                .map(
                  (item, index) =>
                    this.renderQuoteItem(
                      item,
                      index
                    )
                )
                .join('')
            : `
              <div class="aa-operations-state">
                Add a service to begin the quote.
              </div>
            `
          }
        </div>

        <div class="aa-quote-builder__footer">
          <p class="aa-quote-builder__legal">
            Acceptance confirms the proposed scope and pricing
            and authorizes Artisan's Avenue to prepare the
            applicable service agreement. Services remain subject
            to execution of the required agreement.
          </p>

          <div>
            ${this.quoteMessage
              ? `
                <span class="aa-quote-builder__message">
                  ${this.escapeHtml(
                    this.quoteMessage
                  )}
                </span>
              `
              : ''
            }

            <button
              class="aa-button aa-button--primary"
              type="submit"
              ${this.quoteSaving ? 'disabled' : ''}
            >
              ${this.quoteSaving
                ? 'Saving...'
                : 'Save Draft Quote'
              }
            </button>
          </div>
        </div>
      </form>
    `;
  }

  renderQuoteItem(
    item,
    index
  ) {
    return `
      <article
        class="aa-quote-item"
        data-aa-quote-item
        data-aa-quote-item-index="${index}"
      >
        <div class="aa-quote-item__top">
          <label>
            <span>Service</span>
            <select
              data-aa-quote-item-field="serviceCatalogId"
              data-aa-quote-service-select
            >
              <option value="">
                Custom Service
              </option>

              ${this.quoteServices.map(
                (service) => `
                  <option
                    value="${this.escapeHtml(service.id)}"
                    ${String(item.serviceCatalogId) === String(service.id) ? 'selected' : ''}
                  >
                    ${this.escapeHtml(
                      service.name
                    )}
                  </option>
                `
              ).join('')}
            </select>
          </label>

          <label>
            <span>Pricing</span>
            <select
              data-aa-quote-item-field="pricingType"
            >
              <option
                value="monthly"
                ${item.pricingType === 'monthly' ? 'selected' : ''}
              >
                Monthly
              </option>
              <option
                value="one_time"
                ${item.pricingType === 'one_time' ? 'selected' : ''}
              >
                One-Time
              </option>
              <option
                value="custom"
                ${item.pricingType === 'custom' ? 'selected' : ''}
              >
                Custom
              </option>
            </select>
          </label>

          <label>
            <span>Price</span>
            <input
              type="number"
              min="0"
              step="0.01"
              data-aa-quote-item-field="unitPrice"
              value="${this.escapeHtml(
                item.unitPrice ?? 0
              )}"
            >
          </label>

          <button
            type="button"
            class="aa-quote-item__remove"
            data-aa-quote-remove-service="${index}"
            aria-label="Remove service"
          >
            Remove
          </button>
        </div>

        <label>
          <span>Service Name</span>
          <input
            type="text"
            data-aa-quote-item-field="serviceName"
            value="${this.escapeHtml(
              item.serviceName || ''
            )}"
            required
          >
        </label>

        <div class="aa-quote-item__split">
          <label>
            <span>Frequency</span>
            <input
              type="text"
              data-aa-quote-item-field="frequency"
              value="${this.escapeHtml(
                item.frequency || ''
              )}"
              placeholder="Monthly, 4x/month, one-time..."
            >
          </label>

          <label>
            <span>Quantity</span>
            <input
              type="number"
              min="0"
              step="1"
              data-aa-quote-item-field="quantity"
              value="${this.escapeHtml(
                item.quantity ?? 1
              )}"
            >
          </label>
        </div>

        <label>
          <span>Short Description</span>
          <textarea
            rows="2"
            data-aa-quote-item-field="description"
          >${this.escapeHtml(
            item.description || ''
          )}</textarea>
        </label>

        <label>
          <span>Detailed Scope</span>
          <textarea
            rows="4"
            data-aa-quote-item-field="scopeText"
            placeholder="Optional detailed project scope"
          >${this.escapeHtml(
            item.scopeText || ''
          )}</textarea>
        </label>
      </article>
    `;
  }

  getAgreementStateFromAddress(address) {
    const value = String(address || '').trim();
    if (!value) return '';
    const abbreviations = {
      AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',
      CO:'Colorado',CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',
      HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',
      KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',
      MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',
      NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',NM:'New Mexico',
      NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
      OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
      SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',
      VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
      DC:'District of Columbia'
    };
    const postal = value.match(/(?:^|[\s,])([A-Za-z]{2})\s+\d{5}(?:-\d{4})?(?:\s|$)/);
    if (postal) return abbreviations[postal[1].toUpperCase()] || '';
    const normalized = value.replace(/[\n\r]+/g, ', ');
    const fullNames = Object.values(abbreviations).sort((a,b)=>b.length-a.length);
    return fullNames.find(name => new RegExp('(?:^|[,\\s])' + name + '(?=\\s*[,\\d]|$)', 'i').test(normalized)) || '';
  }

  getSignwellClientBusinessDetails(clientId) {
    const client = this.clients.find((row) => String(row.id) === String(clientId));
    if (!client) return { legalBusinessName: '', businessAddress: '' };
    const config = client.client_configuration &&
      typeof client.client_configuration === 'object'
        ? client.client_configuration : {};
    const businessAddress = String(config.mailing_address || client.mailing_address || '').trim();
    const stateOfOrganization = String(config.state_of_organization || '').trim() ||
      this.getAgreementStateFromAddress(businessAddress);
    return {
      // Suggested organization state comes from the address unless a legal
      // formation state is saved. Legal correctness is reviewed before sending.
      legalBusinessName: String(client.business_name || '').trim(),
      businessAddress,
      stateOfOrganization
    };
  }

  getSignwellClientSigner(clientId) {
    const client = this.clients.find(
      (row) => String(row.id) === String(clientId)
    );
    if (!client) return { name: '', email: '' };

    const config = client.client_configuration &&
      typeof client.client_configuration === 'object'
        ? client.client_configuration : {};

    // Only use the primary contact stored on the client account.
    // Never infer the legal signer from the store's business name.
    return {
      name: String(config.primary_contact || client.primary_contact || '').trim(),
      email: String(config.email || client.email || '').trim()
    };
  }

  syncSignwellDraftFields(form) {
    if (!form) return;
    const next = { ...this.signwellDraftFields };
    for (const name of Object.keys(next)) {
      const input = form.elements.namedItem(name);
      if (input && typeof input.value === 'string') {
        next[name] = input.value;
      }
    }
    this.signwellDraftFields = next;
  }

  agreementFrequencyOptions() {
    return [
      ['per week','Per week'], ['per month','Per month'],
      ['per quarter','Per quarter'], ['per year','Per year'],
      ['as needed','As needed'], ['per project','Per project']
    ];
  }

  normalizeAgreementFrequency(unit) {
    const value = String(unit || '').toLowerCase().trim();
    if (value.includes('week')) return 'per week';
    if (value.includes('month')) return 'per month';
    if (value.includes('quarter')) return 'per quarter';
    if (value.includes('year')) return 'per year';
    if (value.includes('project')) return 'per project';
    return 'as needed';
  }

  syncSignwellDraftServicesFromDom(form) {
    if (!form) return;
    const rows = Array.from(form.querySelectorAll('[data-aa-agreement-service-row]'));
    this.signwellDraftServices = rows.map((row) => ({
      serviceCatalogId: row.querySelector('[data-aa-agreement-service-id]')?.value || '',
      quantity: Number(row.querySelector('[data-aa-agreement-service-quantity]')?.value || 0),
      frequency: row.querySelector('[data-aa-agreement-service-frequency]')?.value || 'per month'
    }));
  }

  syncSignwellLibraryValues(form) {
    if (!form) return;
    const values = { ...this.signwellLibraryValues };
    const fields = {
      name: '[data-aa-signwell-library-name]',
      category: '[data-aa-signwell-library-category]',
      frequency: '[data-aa-signwell-library-frequency]',
      description: '[data-aa-signwell-library-description]'
    };
    for (const [key, selector] of Object.entries(fields)) {
      const input = form.querySelector(selector);
      if (input) values[key] = input.value;
    }
    this.signwellLibraryValues = values;
  }

  async loadSignwellClientPackage(clientId) {
    const seq = ++this.signwellPackageRequest;
    this.signwellDraftServices = [];
    this.signwellServicesMessage = '';
    if (!clientId) { this.signwellServicesBusy = false; return; }
    this.signwellServicesBusy = true;
    this.render();
    try {
      const services = await window.AASupabaseClient.getTaskPackageServices({ clientId });
      if (seq !== this.signwellPackageRequest) return;
      const available = new Set(this.signwellServiceCatalog.map((row) => String(row.id)));
      const seen = new Set();
      let duplicateCount = 0;
      const included = [];
      for (const row of services) {
        const id = String(row.serviceCatalogId || '');
        // Retired/removed services are not part of this agreement draft.
        if (!id || !available.has(id)) continue;
        if (seen.has(id)) { duplicateCount++; continue; }
        seen.add(id);
        included.push(row);
      }
      this.signwellDraftServices = included.map((row) => ({
        serviceCatalogId: row.serviceCatalogId,
        quantity: Math.max(1, Number(row.quantity || 1)),
        frequency: this.normalizeAgreementFrequency(row.unit)
      }));
      // The active client package is never mutated by agreement preparation.
      // Multiple active entries for the same service are shown once.
      if (duplicateCount) this.signwellServicesMessage =
        duplicateCount + (duplicateCount === 1
          ? ' repeated package entry was shown once. Confirm its quantity and frequency.'
          : ' repeated package entries were shown once each. Confirm quantities and frequencies.');
      if (!services.length || (!included.length && !duplicateCount)) this.signwellServicesMessage =
        'No active package services available. Add the services for this agreement below.';
    } catch (error) {
      if (seq !== this.signwellPackageRequest) return;
      this.signwellServicesMessage = 'Unable to load the current package. Choose agreement services manually.';
    } finally {
      if (seq === this.signwellPackageRequest) {
        this.signwellServicesBusy = false;
        this.render();
      }
    }
  }

  renderSignwellServiceLibrary() {
    if (!this.signwellLibraryOpen) return '';
    const values = this.signwellLibraryValues;
    const options = this.agreementFrequencyOptions();
    return `
      <div class="aa-signwell-library" data-aa-signwell-library>
        <div class="aa-signwell-library__heading">
          <div><h5>Manage Service Library</h5>
            <p>Available to all clients. Deleting retires a service without erasing its history.</p>
          </div>
          <button type="button" class="aa-button aa-button--secondary" data-aa-signwell-library-close>Done</button>
        </div>
        <div class="aa-signwell-library__fields">
          <label><span>Service Name</span>
            <input type="text" data-aa-signwell-library-name
              value="${this.escapeHtml(values.name)}" placeholder="New service name">
          </label>
          <label><span>Default Frequency</span>
            <select data-aa-signwell-library-frequency>
              ${options.map(([value,label]) => `<option value="${value}" ${values.frequency === value ? 'selected' : ''}>${label}</option>`).join('')}
            </select>
          </label>
          <label><span>Category</span>
            <input type="text" data-aa-signwell-library-category value="${this.escapeHtml(values.category)}">
          </label>
          <label><span>Description (optional)</span>
            <input type="text" data-aa-signwell-library-description value="${this.escapeHtml(values.description)}">
          </label>
        </div>
        <div class="aa-signwell-library__actions">
          <button type="button" class="aa-button aa-button--primary"
            data-aa-signwell-library-save ${this.signwellLibraryBusy ? 'disabled' : ''}>
            ${this.signwellLibraryEditId ? 'Save Service Changes' : 'Add to Service Library'}
          </button>
          ${this.signwellLibraryEditId ? '<button type="button" class="aa-button aa-button--secondary" data-aa-signwell-library-reset>Cancel Editing</button>' : ''}
        </div>
        ${this.signwellLibraryMessage ? '<p class="aa-signwell-library__message" role="status">' + this.escapeHtml(this.signwellLibraryMessage) + '</p>' : ''}
        <div class="aa-signwell-library__list">
          ${this.signwellServiceCatalog.map(service => `
            <div class="aa-signwell-library__item">
              <div><strong>${this.escapeHtml(service.name)}</strong>
                <span>${this.escapeHtml(service.defaultUnitLabel || 'per month')}</span></div>
              <div class="aa-signwell-library__item-actions">
                <button type="button" class="aa-button aa-button--secondary"
                  data-aa-signwell-library-edit="${this.escapeHtml(service.id)}">Edit</button>
                ${String(this.signwellLibraryDeleteId) === String(service.id)
                  ? `<button type="button" class="aa-button aa-signwell-drafts__danger"
                      data-aa-signwell-library-confirm-delete="${this.escapeHtml(service.id)}">Confirm Delete</button>
                      <button type="button" class="aa-button aa-button--secondary" data-aa-signwell-library-cancel-delete>Cancel</button>`
                  : `<button type="button" class="aa-button aa-signwell-drafts__delete"
                      data-aa-signwell-library-delete="${this.escapeHtml(service.id)}">Delete</button>`}
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  addendumRequiredApiIds() {
    return [
      'Effective Date', 'Original Agreement Date', 'Client Business name',
      'Client Business Name Sign Area', 'Client State of Organization',
      'Client Business Address', 'Addendum Change 1', 'Addendum Change 2',
      'Addendum Change 3', 'Updated Monthly Fee', 'Updated Management Fee',
      'One-Time Fee', 'Revised Exhibit A Services'
    ];
  }

  async refreshAddendumConnection(useFreshVerification = false) {
    if (this.signwellAddendumStatus.state === 'checking') return;
    this.signwellAddendumStatus = {state:'checking',message:'Checking the saved SignWell addendum template...'};
    try {
      // The annual Exhibit A verification already checks BOTH SignWell templates.
      // Reuse that result only within the same form-open verification sequence.
      const result = useFreshVerification && this.signwellExhibitStatus.state === 'ready' &&
        Array.isArray(this.signwellTest?.templates)
        ? {templates:this.signwellTest.templates,
           allTemplatesVerified:this.signwellTest.state === 'connected'}
        : await window.AASupabaseClient.verifySignwellConfiguredTemplates();
      const templates = Array.isArray(result.templates) ? result.templates : [];
      this.signwellTest = {
        state: result.allTemplatesVerified ? 'connected' : 'partial',
        message:'', templates
      };
      const addendum = templates.find(item => item.type === 'addendum');
      const allFields = Array.isArray(addendum?.fillableFields) ? addendum.fillableFields : [];
      const missing = this.addendumRequiredApiIds().filter(name =>
        allFields.filter(item => String(item.apiId || '').trim().toLowerCase() === name.toLowerCase()).length !== 1);
      this.signwellAddendumStatus = addendum?.verified && !missing.length
        ? {state:'ready',message:'All 13 addendum fields verified. Review the dates, changes and complete revised package.'}
        : {state:'missing',message: !addendum?.verified
            ? 'SignWell could not verify the saved Addendum template.'
            : 'Check these Advanced API IDs in SignWell: ' + missing.join(', ')};
    } catch (_) {
      this.signwellAddendumStatus = {
        state:'error',message:'SignWell could not check the addendum fields. Select Check Addendum Fields to retry.'
      };
    }
  }

  async refreshAnnualExhibitAConnection() {
    this.signwellExhibitStatus = {state:'checking',message:'Checking SignWell template...'};
    try {
      const verified = await window.AASupabaseClient.verifySignwellConfiguredTemplates();
      const templates = Array.isArray(verified.templates) ? verified.templates : [];
      this.signwellTest = {
        state: verified.allTemplatesVerified ? 'connected' : 'partial',
        message: '',
        templates
      };
      const annual = templates.find(item => item.type === 'service_agreement');
      if (!annual?.verified) {
        this.signwellExhibitStatus = {
          state: 'error',
          message: 'The Annual Agreement template could not be verified in SignWell. Try Check Again.'
        };
        return;
      }
      const matches = (annual.fillableFields || []).filter(item =>
        String(item.apiId || '').trim() === 'Exhibit A Services');
      if (matches.length !== 1 || matches[0].type !== 'text') {
        this.signwellExhibitStatus = {
          state: 'missing',
          message: 'Exhibit A is not connected yet. On the page 6 Text field, set Advanced API ID to Exhibit A Services, save in SignWell, then select Check Again.'
        };
        return;
      }
      const role = String(matches[0].placeholderName || '')
        .toLowerCase().replace(/[^a-z0-9]/g, '');
      if (role === 'client' || role === 'artisansavenuesigner2') {
        this.signwellExhibitStatus = {
          state: 'missing',
          message: 'Exhibit A is assigned to the wrong signer. Assign it to Artisan\'s Avenue Signer 1 (Tiffany), save, then Check Again.'
        };
        return;
      }
      this.signwellExhibitStatus = {
        state: 'ready',
        message: 'Exhibit A Services is connected in SignWell. The next test draft will place the full service list on page 6 and a short reference on page 3.'
      };
    } catch (_) {
      this.signwellExhibitStatus = {
        state: 'error',
        message: 'Could not check the latest Exhibit A field in SignWell. Try Check Again.'
      };
    }
  }

  renderSignwellServiceSection() {
    const rows = this.signwellDraftServices || [];
    const options = this.agreementFrequencyOptions();
    const catalog = this.signwellServiceCatalog || [];
    return `
      <section class="aa-signwell-draft__section">
        <div class="aa-signwell-draft__section-title">
          <div>
            <h4>Services & Frequency</h4>
            <p>Start with the client's current package. Adjust these items for this agreement only.</p>
          </div>
          <button type="button" class="aa-button aa-button--secondary"
            data-aa-signwell-library-toggle aria-expanded="${this.signwellLibraryOpen ? 'true' : 'false'}">
            ${this.signwellLibraryOpen ? 'Close Service Library' : 'Manage Service Library'}
          </button>
        </div>
        ${this.renderSignwellServiceLibrary()}
        ${this.signwellServicesMessage ? '<p class="aa-signwell-draft__notice" role="status">' +
            this.escapeHtml(this.signwellServicesMessage) + '</p>' : ''}
        ${this.signwellServicesBusy
          ? '<p class="aa-signwell-draft__hint">Loading the client\'s package services...</p>'
          : `
            <div class="aa-signwell-service-list">
              ${rows.map((entry,i) => `
                <div class="aa-signwell-service-row" data-aa-agreement-service-row>
                  <label><span>Service</span>
                    <select data-aa-agreement-service-id required>
                      <option value="">Select service</option>
                      ${catalog.map(service => `<option value="${this.escapeHtml(service.id)}"
                        ${String(entry.serviceCatalogId) === String(service.id) ? 'selected' : ''}>${this.escapeHtml(service.name)}</option>`).join('')}
                    </select>
                  </label>
                  <label><span>Quantity</span>
                    <input data-aa-agreement-service-quantity type="number" min="1" max="10000"
                      value="${Number(entry.quantity || 1)}" required>
                  </label>
                  <label><span>Frequency</span>
                    <select data-aa-agreement-service-frequency>
                      ${options.map(([value,label]) => `<option value="${value}" ${entry.frequency === value ? 'selected' : ''}>${label}</option>`).join('')}
                    </select>
                  </label>
                  <button type="button" class="aa-button aa-button--secondary" data-aa-agreement-remove-service="${i}">
                    Remove
                  </button>
                </div>
              `).join('')}
            </div>
            <div class="aa-signwell-service-footer">
              <button type="button" class="aa-button aa-button--secondary" data-aa-agreement-add-service>
                + Add Service
              </button>
              <button type="button" class="aa-button aa-button--secondary" data-aa-signwell-library-add-new>
                Create New Service
              </button>
              ${!rows.length ? '<p class="aa-signwell-draft__hint">Choose at least one service for the agreement.</p>' : ''}
            </div>
          `}
        <p class="aa-signwell-draft__hint">These quantities and frequencies are saved with this agreement only. They do not change the active client package.</p>
        ${this.signwellDraftFields.templateType === 'addendum'
          ? '<p class="aa-signwell-draft__hint"><strong>Revised Exhibit A:</strong> Include every service the client will receive after this change. It will replace the earlier Exhibit A.</p>'
          : ''}
        ${this.signwellDraftFields.templateType === 'service_agreement'
          ? `<div class="aa-signwell-draft__notice" role="status">
               <strong>Exhibit A (page 6): ${this.signwellExhibitStatus.state === 'ready' ? 'Connected' :
                 this.signwellExhibitStatus.state === 'checking' ? 'Checking...' :
                 this.signwellExhibitStatus.state === 'missing' ? 'Needs setup' : 'Not verified'}</strong>
               <p>${this.escapeHtml(this.signwellExhibitStatus.message ||
                 'Headquarters needs to confirm the Exhibit A Services field before another test draft.')}</p>
               <button type="button" class="aa-button aa-button--secondary" data-aa-signwell-check-exhibit
                 ${this.signwellExhibitStatus.state === 'checking' ? 'disabled' : ''}>
                 ${this.signwellExhibitStatus.state === 'checking' ? 'Checking...' : 'Check Again'}
               </button>
             </div>`
          : ''}
      </section>
    `;
  }

  renderSignwellDraftForm() {
    if (!this.signwellDraftOpen) return '';
    const fields = this.signwellDraftFields;
    const selectedClient = this.clients.find(
      (client) => String(client.id) === String(fields.clientId)
    );
    const missingName = Boolean(selectedClient) && !fields.clientName.trim();
    const missingEmail = Boolean(selectedClient) && !fields.clientEmail.trim();
    const needsClientDetails = missingName || missingEmail;
    const mappedTemplate = this.signwellTest.templates?.find(
      (t) => t.type === fields.templateType
    ) || this.signwellConfiguration?.templates?.find(
      (t) => t.type === fields.templateType
    );
    const mappedFields = mappedTemplate?.configuredMapping || {};
    const addendumApiIds = this.addendumRequiredApiIds();
    const requiredAnnual = [
      'legalBusinessName','businessAddress','stateOfOrganization',
      'effectiveDate','expirationDate','servicesSummary','packageTotal'
    ];
    const isAddendum = fields.templateType === 'addendum';
    const matchedAddendum = this.signwellTest.templates?.find(item => item.type === 'addendum');
    const knownApiIds = Array.isArray(matchedAddendum?.fillableFields)
      ? matchedAddendum.fillableFields.map(field => String(field.apiId || '').trim().toLowerCase())
      : [];
    const required = isAddendum ? addendumApiIds : requiredAnnual;
    const mappedFieldCount = isAddendum
      ? addendumApiIds.filter(name => knownApiIds.includes(name.toLowerCase())).length
      : requiredAnnual.filter(key =>
          Array.isArray(mappedFields[key]) ? mappedFields[key].length > 0 : Boolean(mappedFields[key])
        ).length;
    const annualLabels = {
      legalBusinessName:'Client legal business name',businessAddress:'Business address',
      stateOfOrganization:'Client state of organization',effectiveDate:'Effective date',
      expirationDate:'Expiration date',servicesSummary:'Services and frequency',
      packageTotal:'Total package price'
    };
    const missingPrefillLabels = isAddendum
      ? addendumApiIds.filter(name => !knownApiIds.includes(name.toLowerCase()))
      : requiredAnnual.filter(key => !(Array.isArray(mappedFields[key])
          ? mappedFields[key].length > 0 : Boolean(mappedFields[key]))).map(key => annualLabels[key]);
    const fieldsVerified = isAddendum
      ? Boolean(matchedAddendum?.verified && mappedFieldCount === addendumApiIds.length)
      : mappedFieldCount === requiredAnnual.length;

    const clientOptions = this.clients.slice().sort((a,b) =>
      String(a.business_name || '').localeCompare(String(b.business_name || ''))
    ).map(client =>
      '<option value="' + this.escapeHtml(client.id) + '"' +
      (String(fields.clientId) === String(client.id) ? ' selected' : '') + '>' +
      this.escapeHtml(client.business_name || 'Unnamed Client') + '</option>'
    ).join('');

    return `
      <form data-aa-signwell-draft-form class="aa-contract-quote-panel aa-signwell-draft"
        data-aa-suggested-state="${this.escapeHtml(this.getAgreementStateFromAddress(fields.businessAddress))}">
        <div class="aa-signwell-draft__heading">
          <div>
            <p class="aa-card__label">SignWell / ${this.signwellPreparationMode === 'live' ? 'Unsigned Live Preparation' : 'Test Preparation'}</p>
            <h3>Prepare an Agreement</h3>
            <p>Review every signer and agreement detail. ${this.signwellPreparationMode === 'live' ? 'This prepares an unsigned live document for approval, but never sends it.' : 'Test drafts are not legally binding and will not email anyone.'}</p>
          </div>
          <div class="aa-signwell-drafts__actions aa-signwell-draft__mode-options"
            aria-label="Agreement preparation mode">
            ${this.signwellLivePreparationEnabled ? `
              <button type="button" class="aa-button aa-button--secondary"
                data-aa-signwell-prepare-mode="test"
                aria-pressed="${this.signwellPreparationMode === 'test'}">Test Draft</button>
              <button type="button" class="aa-button aa-button--secondary"
                data-aa-signwell-prepare-mode="live"
                aria-pressed="${this.signwellPreparationMode === 'live'}">Live Unsigned Draft</button>
            ` : `
              <span class="aa-signwell-draft__mode-selected">Test Draft · Selected</span>
              <span class="aa-signwell-draft__mode-locked"
                aria-label="Live unsigned agreements are locked until internal signing tests pass">
                Live Unsigned Draft · Locked
              </span>
            `}
          </div>
        </div>
        ${this.signwellDraftMessage ? '<p class="aa-signwell-draft__notice" role="status" aria-live="polite">' +
          this.escapeHtml(this.signwellDraftMessage) + '</p>' : ''}
        ${isAddendum ? `<div class="aa-signwell-draft__notice" role="status" aria-live="polite">
          <strong>Addendum Fields: ${mappedFieldCount}/${required.length} ${fieldsVerified ? 'Connected' : 'Not Ready'}</strong>
          <p>${this.escapeHtml(this.signwellAddendumStatus.message ||
            (missingPrefillLabels.length ? 'Checking: ' + missingPrefillLabels.join(', ') :
             'Ready for review.'))}</p>
          <button type="button" class="aa-button aa-button--secondary"
            data-aa-signwell-check-addendum ${this.signwellAddendumStatus.state === 'checking' ? 'disabled' : ''}>
            ${this.signwellAddendumStatus.state === 'checking' ? 'Checking...' : 'Check Addendum Fields'}
          </button>
        </div>` : ''}

        ${this.signwellPreparationMode === 'live' && isAddendum ? `
          <section class="aa-signwell-draft__section">
            <h4>Approved Package Change</h4>
            <p>Live addendums must be attached to the original approved request.</p>
            <label>Approved request
              <select name="packageChangeRequestId" required>
                <option value="">Select approved package change</option>
                ${this.signwellApprovedChanges.filter(item =>
                  String(item.client_id) === String(fields.clientId)).map(item =>
                  '<option value="' + this.escapeHtml(item.id) + '"' +
                  (String(item.id) === String(fields.packageChangeRequestId) ? ' selected' : '') + '>' +
                  this.escapeHtml(item.service_name + ': ' + item.requested_package_value) +
                  '</option>').join('')}
              </select>
            </label>
          </section>` : ''}
        <section class="aa-signwell-draft__section">
          <h4>Document & Client</h4>
          <div class="aa-signwell-draft__grid">
            <label>
              <span>Document Type</span>
              <select name="templateType" required>
                <option value="service_agreement" ${fields.templateType === 'service_agreement' ? 'selected' : ''}>Annual Services Agreement</option>
                <option value="addendum" ${fields.templateType === 'addendum' ? 'selected' : ''}>Package Change Addendum</option>
              </select>
            </label>
            <label>
              <span>Client Account</span>
              <select name="clientId" required>
                <option value="">Select a client</option>
                ${clientOptions}
              </select>
            </label>
          </div>
        </section>

        <section class="aa-signwell-draft__section">
          <div class="aa-signwell-draft__section-title">
            <h4>Client Signer</h4>
            <span>From the client's saved account information</span>
          </div>
          <div class="aa-signwell-draft__grid">
            <label>
              <span>Authorized Signer's Name</span>
              <input name="clientName" type="text" required autocomplete="off"
                placeholder="Select a client to auto-fill"
                value="${this.escapeHtml(fields.clientName)}">
            </label>
            <label>
              <span>Signer's Email</span>
              <input name="clientEmail" type="email" required autocomplete="off"
                placeholder="Select a client to auto-fill"
                value="${this.escapeHtml(fields.clientEmail)}">
            </label>
          </div>
          ${this.signwellPreparationMode === 'test' &&
            this.signwellConfiguration?.qaClientEmail
            ? `<div class="aa-signwell-drafts__actions">
                <button type="button" class="aa-button aa-button--secondary"
                  data-aa-signwell-use-qa-client>Use Internal Test Client</button>
                <span class="aa-signwell-draft__hint">
                  Uses the designated internal Gmail inbox for this unsigned test draft only.
                </span>
              </div>` : ''}
          ${this.signwellPreparationMode === 'test' &&
            fields.clientEmail?.toLowerCase() ===
              String(this.signwellConfiguration?.qaClientEmail || '').toLowerCase() &&
            this.signwellConfiguration?.qaClientEmail
            ? '<p class="aa-signwell-draft__notice">Internal QA signer selected. The client account contact is unchanged. No emails will be sent.</p>'
            : `${needsClientDetails
              ? '<p class="aa-signwell-draft__notice" role="status">This client is missing a ' +
                  (missingName && missingEmail ? 'primary contact name and email' :
                    missingName ? 'primary contact name' : 'primary contact email') +
                  ' in Clients. Confirm the authorized signer before proceeding.</p>'
              : selectedClient
                ? '<p class="aa-signwell-draft__hint">Filled from the client record. Confirm this person is authorized to sign.</p>'
                : '<p class="aa-signwell-draft__hint">Choose a client to fill their saved primary contact details.</p>'
            }`}
          }
        </section>

        <section class="aa-signwell-draft__section">
          <div class="aa-signwell-draft__section-title">
            <h4>Business & Agreement Dates</h4>
            <span>Review before preparing the document</span>
          </div>
          <div class="aa-signwell-draft__grid">
            <label>
              <span>Legal Business Name</span>
              <input type="text" name="legalBusinessName" required
                placeholder="Confirm the legal name" maxlength="250"
                value="${this.escapeHtml(fields.legalBusinessName)}">
            </label>
            <label>
              <span>${fields.templateType === 'addendum' ? 'Addendum Effective Date' : 'Agreement Effective Date'}</span>
              <input type="date" name="effectiveDate" required
                value="${this.escapeHtml(fields.effectiveDate)}">
            </label>
            <label class="aa-signwell-draft__wide">
              <span>Client Business Address</span>
              <textarea rows="3" name="businessAddress" required maxlength="650"
                placeholder="Street address, suite, city, state, ZIP">${this.escapeHtml(fields.businessAddress)}</textarea>
            </label>
            <label>
              <span>State of Legal Organization</span>
              <input name="stateOfOrganization" type="text" required maxlength="70"
                placeholder="Defaults to the client's address state"
                value="${this.escapeHtml(fields.stateOfOrganization)}">
              <small>Suggested from the business address. Change it if the company was formed elsewhere.</small>
            </label>
            ${fields.templateType === 'service_agreement'
              ? `<label>
                  <span>Agreement Expiration Date</span>
                  <input type="date" name="expirationDate" required
                    min="${this.escapeHtml(fields.effectiveDate)}"
                    value="${this.escapeHtml(fields.expirationDate)}">
                </label>`
              : ''}
            ${isAddendum ? `<label>
                <span>Original Agreement Effective Date</span>
                <input type="date" name="originalAgreementDate" required
                  max="${this.escapeHtml(fields.effectiveDate)}"
                  value="${this.escapeHtml(fields.originalAgreementDate)}">
                <small>Use the date of the original signed services agreement, not the addendum date.</small>
              </label>` : ''}
          </div>
          ${selectedClient && !fields.businessAddress.trim()
            ? '<p class="aa-signwell-draft__notice" role="status">Business address is missing from this client profile. Confirm the full address before preparing an agreement.</p>'
            : '<p class="aa-signwell-draft__hint">Business details come from the client profile. Dates are entered for this agreement, not guessed.</p>'}
          <p class="aa-signwell-draft__hint">The agreement uses this legal formation state. Check the client's business registration if you are unsure. Don't guess.</p>
        </section>

        ${isAddendum ? `<section class="aa-signwell-draft__section">
          <div class="aa-signwell-draft__section-title">
            <div>
              <h4>What is changing?</h4>
              <p>Describe up to three specific changes. Revised Exhibit A will show the entire updated package.</p>
            </div>
          </div>
          <div class="aa-signwell-draft__grid">
            ${[1,2,3].map(number => `<label class="aa-signwell-draft__wide">
              <span>Change ${number}${number === 1 ? ' (required)' : ' (optional)'}</span>
              <textarea name="addendumChange${number}" rows="2" maxlength="190"
                ${number === 1 ? 'required' : ''}
                placeholder="${number === 1 ? 'Example: Increase social posts from 3 to 4 per week' : 'Additional change, if any'}">${this.escapeHtml(fields['addendumChange' + number])}</textarea>
            </label>`).join('')}
          </div>
        </section>` : ''}

        ${this.renderSignwellServiceSection()}

        <section class="aa-signwell-draft__section">
          <div class="aa-signwell-draft__section-title">
            <div>
              <h4>${isAddendum ? 'Updated Compensation' : 'Client Package Price'}</h4>
              <p>${isAddendum ? 'Enter the revised monthly fee and any applicable additional fees.' : 'One total for the entire package. Individual service prices are never shown on this agreement.'}</p>
            </div>
          </div>
          <div class="aa-signwell-draft__grid">
            <label>
              <span>${isAddendum ? 'Updated Monthly Fee (USD)' : 'Total Package Price (USD)'}</span>
              <input type="number" inputmode="decimal" name="packagePrice"
                min="0" step="0.01" max="10000000"
                placeholder="e.g. 699.00" required
                value="${this.escapeHtml(fields.packagePrice)}">
            </label>
            <label>
              <span>Billing Frequency</span>
              <select name="billingPeriod" required>
                ${(isAddendum ? [['per month','Per month']] : [['per month','Per month'],['per quarter','Per quarter'],
                   ['per year','Per year'],['per week','Per week'],
                   ['one time','One-time']]).map(([value,label]) =>
                     `<option value="${value}" ${fields.billingPeriod === value ? 'selected' : ''}>${label}</option>`
                   ).join('')}
              </select>
            </label>
          </div>
          <p class="aa-signwell-draft__hint">Individual service rates are never listed in the client-facing agreement.</p>
          <div class="aa-signwell-draft__grid">
            <label>
              <span>${isAddendum ? 'Updated Management Fee (USD)' : 'Management Fee (Section 4b, USD)'}</span>
              <input type="number" name="managementFee" min="0" step="0.01" max="10000000"
                inputmode="decimal" required value="${this.escapeHtml(fields.managementFee || '0.00')}">
              <small>Defaults to $0.00 when not applicable.</small>
            </label>
            ${isAddendum ? `<label>
              <span>One-Time Fee (USD)</span>
              <input type="number" name="oneTimeFee" min="0" step="0.01" max="10000000"
                inputmode="decimal" required value="${this.escapeHtml(fields.oneTimeFee || '0.00')}">
              <small>Defaults to $0.00 when not applicable.</small>
            </label>` : ''}
          </div>
          <p class="aa-signwell-draft__hint">If an additional fee is above zero, confirm it is clearly distinguished from the revised package price.</p>
        </section>

        <section class="aa-signwell-draft__section">
          <div class="aa-signwell-draft__section-title">
            <h4>Artisan's Avenue Signers</h4>
            <span>From your internal team accounts</span>
          </div>
          <div class="aa-signwell-draft__signer-grid">
            <div class="aa-signwell-draft__signer">
              <strong>Tiffany</strong>
              <div class="aa-signwell-draft__grid">
                <label><span>Full Name</span>
                  <input name="owner1Name" type="text" required
                    value="${this.escapeHtml(fields.owner1Name)}">
                </label>
                <label><span>Signing Email</span>
                  <input name="owner1Email" type="email" required
                    value="${this.escapeHtml(fields.owner1Email)}"
                    placeholder="Email not saved in account">
                </label>
              </div>
            </div>
            <div class="aa-signwell-draft__signer">
              <strong>Whitney</strong>
              <div class="aa-signwell-draft__grid">
                <label><span>Full Name</span>
                  <input name="owner2Name" type="text" required
                    value="${this.escapeHtml(fields.owner2Name)}">
                </label>
                <label><span>Signing Email</span>
                  <input name="owner2Email" type="email" required
                    value="${this.escapeHtml(fields.owner2Email)}"
                    placeholder="Email not saved in account">
                </label>
              </div>
            </div>
          </div>
        </section>

        <div class="aa-signwell-draft__footer">
          <p>${this.signwellPreparationMode === 'live' ? 'Unsigned live draft. Approval and separate sending required.' : 'Test mode. No signatures requested.'} ${mappedFieldCount}/${required.length} ${isAddendum ? 'addendum fields verified in SignWell' : 'document details connected'}.
            ${missingPrefillLabels.length
              ? 'Not ready: ' + this.escapeHtml(missingPrefillLabels.join(', ')) +
                '. SignWell fields need attention before another test document can be created.'
              : 'Connections are present. The actual draft still needs to be inspected before real sending.'}
          </p>
          <div class="aa-signwell-draft__actions">
            <button type="button" class="aa-button aa-button--secondary"
              data-aa-signwell-close-test-draft>Cancel</button>
            <button type="submit" class="aa-button aa-button--primary"
              ${this.signwellDraftBusy || !fieldsVerified ||
                this.signwellExhibitStatus.state === 'checking' ||
                (fields.templateType === 'service_agreement' && this.signwellExhibitStatus.state !== 'ready') ||
                (fields.templateType === 'addendum' && this.signwellAddendumStatus.state !== 'ready')
                ? 'disabled' : ''}>
              ${this.signwellDraftBusy ? 'Preparing...' : this.signwellPreparationMode === 'live' ? 'Create Unsigned Live Draft' : 'Create Test Draft'}
            </button>
          </div>
        </div>
      </form>
    `;
  }

  renderSignwellReviewNextStep() {
    if (this.signwellDraftOpen) return '';
    // Still display the next step after a refresh, using the latest
    // populated test draft already tracked in Headquarters.
    const draft = this.signwellLastPreparedId
      ? this.signwellDrafts.find(item => String(item.id) === String(this.signwellLastPreparedId))
      : this.signwellDrafts.find(item => Number(item.prefilledFieldCount || 0) > 0);
    if (!draft || Number(draft.prefilledFieldCount || 0) < 1) return '';
    const count = Number(draft.prefilledFieldCount);
    return `
      <section class="aa-signwell-review-next" data-aa-signwell-ready-to-review role="status" aria-live="polite">
        <div>
          <p class="aa-card__label">Test Draft Ready for Review</p>
          <h3>Next: Review or Edit Your Test Draft</h3>
          <p>${Number.isFinite(count) && count > 0
            ? count + ' fields submitted for prefill.'
            : 'Draft prepared.'}
            No signature invitations sent. Open the draft to confirm the actual document is filled correctly.</p>
        </div>
        <button type="button" class="aa-button aa-button--primary"
          data-aa-signwell-open-draft="${this.escapeHtml(draft.id)}">
          Edit Draft in SignWell
        </button>
      </section>
    `;
  }

  async refreshSignwellDrafts() {
    if (this.signwellDraftsRefreshing || this.signwellBulkDeleting) return;
    this.signwellDraftsRefreshing = true;
    this.signwellDraftLoadError = '';
    this.render();
    try {
      const latest = await window.AASupabaseClient.getSignwellTestDrafts();
      if (!Array.isArray(latest?.drafts)) {
        throw new Error('SignWell draft list returned without its records.');
      }
      this.signwellDrafts = latest.drafts;
      this.signwellDraftTotal = Number.isFinite(Number(latest.totalTestDrafts))
        ? Number(latest.totalTestDrafts) : this.signwellDrafts.length;
      this.signwellDraftRecoveryWarning = String(latest.recoveryWarning || '');
      if (!this.signwellDrafts.length) this.signwellLastPreparedId = '';
    } catch (error) {
      this.signwellDraftLoadError = error?.message || 'Unable to retrieve agreement drafts.';
      // Keep the last known records. Never turn a failed load into an empty list.
    } finally {
      this.signwellDraftsRefreshing = false;
      this.render();
    }
  }

  renderSignwellDraftRows() {
    const drafts = this.signwellDrafts.slice(0, 12);
    const total = Math.max(this.signwellDrafts.length, Number(this.signwellDraftTotal || 0));
    const bulkReady = total > 0 && !this.signwellDraftLoadError &&
      !this.signwellDraftsRefreshing && total === this.signwellDrafts.length;
    const newestPopulatedId = drafts.find(draft => Number(draft.prefilledFieldCount || 0) > 0)?.id || '';
    return `
      <section class="aa-signwell-drafts" aria-label="Recent agreement drafts">
        <div class="aa-signwell-drafts__header">
          <div>
            <h3>Recent Agreements</h3>
            <p>Unsigned test drafts can be deleted. Signed documents are preserved as records.</p>
          </div>
          <div class="aa-signwell-drafts__header-actions">
            <button type="button" class="aa-button aa-button--secondary"
              data-aa-signwell-refresh-drafts ${this.signwellDraftsRefreshing || this.signwellBulkDeleting ? 'disabled' : ''}>
              ${this.signwellDraftsRefreshing ? 'Refreshing...' : 'Refresh Agreements'}
            </button>
          ${bulkReady ? `<button type="button" class="aa-button aa-signwell-drafts__delete"
            data-aa-signwell-delete-all-start
            ${this.signwellBusyId || this.signwellBulkDeleting ? 'disabled' : ''}>
            Delete All Test Drafts (${total})
          </button>` : ''}
          </div>
        </div>
        ${this.signwellDraftLoadError
          ? `<p class="aa-signwell-drafts__message" role="alert">Unable to load agreement drafts: ${this.escapeHtml(this.signwellDraftLoadError)}. The records have not been deleted. Select Refresh Agreements to try again.</p>`
          : ''}
        ${total > this.signwellDrafts.length
          ? `<p class="aa-signwell-drafts__message">Only ${this.signwellDrafts.length} of ${total} drafts are loaded. Bulk deletion is unavailable until every test draft is loaded.</p>`
          : ''}
        ${this.signwellBulkDeleteConfirm && bulkReady
          ? `<div class="aa-signwell-drafts__bulk-confirm" role="group" aria-label="Confirm deletion of all test drafts">
               <p><strong>Delete all ${total} unsigned test drafts?</strong> They will be removed from SignWell and Headquarters. This cannot be undone. Signed agreements will not be deleted.</p>
               <button type="button" class="aa-button aa-signwell-drafts__danger"
                 data-aa-signwell-delete-all-confirm ${this.signwellBulkDeleting ? 'disabled' : ''}>
                 Yes, Delete ${total} Test Drafts
               </button>
               <button type="button" class="aa-button aa-button--secondary"
                 data-aa-signwell-delete-all-cancel ${this.signwellBulkDeleting ? 'disabled' : ''}>Cancel</button>
             </div>`
          : ''}
        ${this.signwellBulkProgressMessage
          ? '<p class="aa-signwell-drafts__message" role="status">' + this.escapeHtml(this.signwellBulkProgressMessage) + '</p>'
          : ''}
        ${this.signwellDraftRecoveryWarning
          ? '<p class="aa-signwell-drafts__message" role="status">' +
              this.escapeHtml(this.signwellDraftRecoveryWarning) + '</p>'
          : ''}
        ${this.signwellDraftActionMessage
          ? '<p class="aa-signwell-drafts__message" role="status">' +
              this.escapeHtml(this.signwellDraftActionMessage) + '</p>'
          : ''}
        ${drafts.length
          ? '<div class="aa-signwell-drafts__list">' +
            drafts.map((draft) => {
              const id = this.escapeHtml(draft.id || '');
              const busy = this.signwellBulkDeleting || String(this.signwellBusyId) === String(draft.id);
              const confirming = String(this.signwellConfirmDeleteId) === String(draft.id);
              const title = draft.template_type === 'addendum'
                ? 'Package Change Addendum' : 'Annual Services Agreement';
              const client = this.getClientName(draft.client_id);
              const when = String(draft.created_at || '').slice(0, 10);
              const prefilled = Number(draft.prefilledFieldCount || 0);
              const unfilledLegacy = prefilled === 0;
              const readyForReview = !unfilledLegacy &&
                String(draft.id) === String(newestPopulatedId);
              return `<div class="aa-signwell-drafts__row${readyForReview ? ' aa-signwell-drafts__row--ready' : ''}">
                <div class="aa-signwell-drafts__description">
                  <strong>${this.escapeHtml(title)}</strong>
                  <span>${this.escapeHtml(client)} · ${this.escapeHtml(when)} · ${unfilledLegacy
                    ? 'Original blank test draft'
                    : readyForReview ? 'Latest prepared test draft · Review next' : 'Test draft'}</span>
                  <span>${unfilledLegacy
                    ? '0 fields prefilled. This is the original draft and will not update when template connections change.'
                    : prefilled + ' fields prefilled when created. Review in SignWell before relying on it.'}</span>
                  ${Array.isArray(draft.service_schedule) && draft.service_schedule.length
                    ? `<span class="aa-signwell-drafts__services">${draft.service_schedule.map(item =>
                        this.escapeHtml(String(item.quantity || 1) + ' ' +
                        String(item.serviceName || 'Service') + ' ' +
                        String(item.frequency || ''))
                      ).join(' · ')}</span>`
                    : ''}
                </div>
                <div class="aa-signwell-drafts__actions">
                  <button type="button" class="aa-button aa-button--secondary"
                    data-aa-signwell-open-draft="${id}" ${busy ? 'disabled' : ''}>
                    ${unfilledLegacy ? 'Open Blank Test Draft' : 'Edit Draft in SignWell'}
                  </button>
                  ${!unfilledLegacy && !this.signwellBulkDeleting
                    ? `<button type="button" class="aa-button aa-button--secondary"
                        data-aa-signwell-revise-draft="${id}" ${busy ? 'disabled' : ''}>
                        Revise in Headquarters
                      </button>` : ''}
                  ${confirming
                    ? `<button type="button" class="aa-button aa-signwell-drafts__danger"
                          data-aa-signwell-confirm-delete="${id}" ${busy ? 'disabled' : ''}>
                          Confirm Delete
                        </button>
                        <button type="button" class="aa-button aa-button--secondary"
                          data-aa-signwell-cancel-delete>Cancel</button>`
                    : `<button type="button" class="aa-button aa-signwell-drafts__delete"
                          data-aa-signwell-start-delete="${id}" ${busy ? 'disabled' : ''}>
                          Delete Test Draft
                        </button>`
                  }
                </div>
              </div>`;
            }).join('') + '</div>'
          : this.signwellDraftsRefreshing
            ? '<p class="aa-signwell-drafts__empty" role="status">Loading unsigned test drafts...</p>'
            : this.signwellDraftLoadError
              ? '<p class="aa-signwell-drafts__empty">Agreement records could not be loaded. Please use Refresh Agreements above.</p>'
              : '<p class="aa-signwell-drafts__empty">No unsigned test drafts are currently listed.</p>'
        }
      </section>
    `;
  }


  async refreshSignwellLiveAgreements(runOneBatch = false, showFeedback = false) {
    // Only an explicit, deliberate click may request one server-side batch.
    // Polling and the ordinary Refresh Agreements button are read-only.
    if (this.signwellLiveRefreshing) return;
    this.signwellLiveRefreshing = true;
    if (showFeedback) {
      this.signwellLiveRefreshNotice = runOneBatch
        ? 'Checking reconciliation availability...' : 'Loading stored agreements...';
      this.render();
    }
    let reconciliationNotice = '';
    try {
      if (runOneBatch) {
        try {
          const before = await window.AASupabaseClient.runSignwellLiveAction(
            'reconciliation-status', null);
          const signing = before?.streams?.find(s => s.stream === 'signing_recovery');
          if (!signing?.enabled) {
            reconciliationNotice = 'Reconciliation is disabled. Stored agreements can still be refreshed.';
          } else if (signing.leaseHeld === true || signing.state === 'processing') {
            reconciliationNotice = 'A reconciliation lease is already processing or reserved. Active work is not independently confirmed. No competing run was started.';
          } else {
            const batch = await window.AASupabaseClient.runSignwellLiveAction('sync-all', null);
            if (batch?.status === 'sweep_complete' && batch.complete === true) {
              reconciliationNotice = 'Current signing-recovery sweep finished. New work may still arrive.';
            } else if (batch?.status === 'partial' && batch.complete === false) {
              const count = Number.isSafeInteger(batch.processed) && batch.processed >= 0
                ? batch.processed : null;
              reconciliationNotice = (count === null ? 'A bounded batch ran.' :
                count + ' agreement(s) processed in this batch.') +
                (batch.ready_for_continuation === true
                  ? ' More work may remain; the lease was yielded for immediate continuation.'
                  : ' More work may remain. Continuation availability is not confirmed.');
            } else if (batch?.status === 'disabled_not_due_or_busy') {
              reconciliationNotice = 'Reconciliation is disabled or busy. No second worker was started.';
            } else {
              reconciliationNotice = 'Reconciliation outcome is uncertain. Check progress before trying again.';
            }
          }
        } catch (_) {
          // The checkpoint is durable. Do not assert a sweep finished after a
          // timeout, a disabled worker, or an interrupted provider operation.
          reconciliationNotice = 'Reconciliation did not confirm completion. Durable progress may be retained; check status.';
        }
      }
      const [listResult, statusResult] = await Promise.allSettled([
        window.AASupabaseClient.getSignwellLiveAgreements(),
        window.AASupabaseClient.runSignwellLiveAction('reconciliation-status', null)
      ]);
      if (statusResult.status === 'fulfilled' &&
          Array.isArray(statusResult.value?.streams)) {
        this.signwellReconciliation = statusResult.value;
        this.signwellReconciliationStatusError = '';
      } else {
        this.signwellReconciliationStatusError = 'Reconciliation progress is unavailable.';
      }
      const data = listResult.status === 'fulfilled' ? listResult.value : null;
      if (!Array.isArray(data?.documents)) {
        this.signwellLiveLoadError = 'Unable to reload stored agreements. The previously loaded list is retained.';
        this.signwellLiveRefreshNotice = reconciliationNotice;
        return;
      }
      this.signwellLiveDocuments = data.documents;
      this.signwellLivePreparationEnabled = data.livePreparationEnabled === true;
      this.signwellLiveSendingEnabled = data.sendingEnabled === true;
      this.signwellLiveCheckedAt = new Date().toISOString();
      this.signwellLiveLoadError = '';
      if (showFeedback) {
        this.signwellLiveRefreshNotice = reconciliationNotice ||
          'Stored agreement list refreshed. This does not verify SignWell signatures.';
      }
    } finally {
      this.signwellLiveRefreshing = false;
      this.render();
    }
  }

  renderSignwellLiveWorkflow() {
    const list = Array.isArray(this.signwellLiveDocuments) ? this.signwellLiveDocuments : [];
    const streams = Array.isArray(this.signwellReconciliation?.streams)
      ? this.signwellReconciliation.streams : [];
    const signing = streams.find(row => row.stream === 'signing_recovery');
    const integrity = streams.find(row => row.stream === 'completed_integrity');
    const readableTime = value => {
      const ms = Date.parse(String(value || ''));
      return Number.isFinite(ms) ? new Intl.DateTimeFormat('en-US',
        {month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}).format(new Date(ms))
        : 'Not recorded';
    };
    const stateLabel = row => {
      if (!row) return 'Unknown';
      if (row.state === 'disabled') return 'Disabled';
      if (row.leaseHeld === true || row.state === 'processing')
        return 'Lease held (active execution not independently confirmed)';
      if (row.stalled || row.recoveryPending)
        return 'Partially complete (expired lease; recovery pending)';
      if (row.state === 'partial')
        return row.continuationReady === true
          ? 'Partially complete (ready for continuation)'
          : 'Partially complete (continuation status unknown)';
      if (row.state === 'sweep_finished') return 'Last sweep finished';
      if (row.state === 'not_started') return 'Not yet started';
      return 'Unknown';
    };
    const needsReview = list.filter(doc =>
      ['sending','send_needs_reconciliation'].includes(doc.status) ||
      Boolean(doc.last_error_code) || (doc.status === 'completed' && !doc.signed_pdf_available));
    const attention = needsReview.length > 0 ||
      streams.some(row => row.attentionRequired === true || row.stalled === true);
    const busy = signing?.leaseHeld === true || signing?.state === 'processing';
    const canRun = Boolean(signing?.enabled) && !busy && !this.signwellLiveRefreshing;
    return `
      <section class="aa-contract-quote-panel aa-signwell-live" aria-label="Agreement approvals and signatures" aria-busy="${this.signwellLiveRefreshing ? 'true' : 'false'}">
        <div class="aa-signwell-drafts__header">
          <div>
            <p class="aa-card__label">Approvals & Signatures</p>
            <h3>Live Agreement Workflow</h3>
            <p>Review, approve, then deliberately send. SignWell handles signing; completed PDFs stay with the client.</p>
          </div>
          <div class="aa-signwell-drafts__header-actions">
            <button type="button" class="aa-button aa-button--secondary"
              data-aa-signwell-refresh-live ${this.signwellLiveRefreshing ? 'disabled' : ''}>
              ${this.signwellLiveRefreshing ? 'Refreshing...' : 'Refresh Agreements'}
            </button>
            <button type="button" class="aa-button aa-button--secondary"
              data-aa-signwell-run-reconciliation ${canRun ? '' : 'disabled'}
              title="Runs at most one bounded signing-recovery batch">
              ${busy ? 'Lease Held' : 'Run One Reconciliation Batch'}
            </button>
          </div>
        </div>
        <div class="aa-signwell-live__progress" role="status" aria-live="polite">
          <p><strong>Last refreshed:</strong> ${this.escapeHtml(readableTime(this.signwellLiveCheckedAt))}
            <span>Stored agreement records only. Not a signature verification.</span></p>
          <p><strong>Reconciliation status:</strong>
            ${this.escapeHtml(signing ? 'Signing recovery: ' + stateLabel(signing) : 'Unknown')}
            · ${this.escapeHtml(integrity ? 'Completed integrity: ' + stateLabel(integrity) : 'Unknown')}
            <span>${streams.some(row => row.mayHaveMore === true)
              ? 'A sweep is unfinished; more work may remain.'
              : 'Pending record count unknown. A finished sweep does not verify every agreement.'}</span></p>
          <p><strong>Last attempted:</strong> ${this.escapeHtml(readableTime(signing?.lastAttemptAt))}
            · <strong>Last sweep completed:</strong> ${this.escapeHtml(readableTime(signing?.lastCompletedAt))}</p>
          <p><strong>Needs attention:</strong> ${attention
            ? this.escapeHtml(needsReview.length ? needsReview.length +
              ' displayed agreement(s) flagged; inspect reconciliation errors.' :
              'Reconciliation needs review.')
            : 'No alerts identified in the currently displayed records. Outstanding work may be unknown.'}</p>
          ${this.signwellReconciliationStatusError
            ? '<p>' + this.escapeHtml(this.signwellReconciliationStatusError) +
              ' Do not assume signing status is current.</p>' : ''}
          ${signing && !signing.enabled
            ? '<p>Background reconciliation is disabled. Refresh Agreements loads stored records only.</p>' : ''}
          ${streams.some(row => row.stalled)
            ? '<p>Reconciliation may have stalled. Durable work is awaiting recovery.</p>' : ''}
        </div>
        ${this.signwellLiveRefreshNotice
          ? '<p class="aa-signwell-drafts__message" role="status" aria-live="polite">' +
            this.escapeHtml(this.signwellLiveRefreshNotice) + '</p>' : ''}
        ${this.signwellLiveLoadError
          ? '<p class="aa-signwell-drafts__message" role="status">' + this.escapeHtml(this.signwellLiveLoadError) + '</p>' : ''}
        ${this.signwellLiveMessage
          ? '<p class="aa-signwell-drafts__message" role="status">' + this.escapeHtml(this.signwellLiveMessage) + '</p>' : ''}
        ${list.length ? '<div class="aa-signwell-drafts__list">' + list.map(doc => {
          const id = this.escapeHtml(doc.id);
          const client = this.getClientName(doc.client_id);
          const busy = this.signwellLiveBusyId === doc.id;
          const statusNames = {
            prepared:'Prepared',awaiting_approval:'Awaiting Approval',approved:'Approved',
            sending:'Sending · Verify Status',send_needs_reconciliation:'Send Needs Review',
            sent:'Sent',viewed:'Viewed',partially_signed:'Awaiting Signatures',
            fully_signed:'Fully Signed',completed:'Completed',declined:'Declined',
            expired:'Expired',canceled:'Canceled',bounced:'Delivery Issue'
          };
          const status = statusNames[doc.status] || String(doc.status || 'Prepared');
          const signers = Array.isArray(doc.signature_recipients) ? doc.signature_recipients : [];
          const isReviewable = ['prepared','awaiting_approval'].includes(doc.status);
          const sendReview = this.signwellLiveSendReview;
          const showSend = sendReview?.id === doc.id;
          return `<div class="aa-signwell-drafts__row aa-signwell-live__row">
            <div class="aa-signwell-drafts__description">
              <strong>${this.escapeHtml(doc.template_type === 'addendum' ? 'Package Change Addendum' : 'Annual Services Agreement')}</strong>
              <span>${this.escapeHtml(client)} · ${this.escapeHtml(status)}</span>
              <span>${this.escapeHtml(String(doc.agreement_details?.effectiveDate || 'Date pending'))}</span>
              ${signers.length ? '<span class="aa-signwell-live__signers">' +
                signers.map(r => this.escapeHtml(String(r.name || 'Signer') + ' (' + String(r.email || 'No email') + ')' +
                  (r.signed ? ' • Signed' : ''))).join(' · ') + '</span>' : ''}
              ${doc.billing_attention_required ? '<span>Billing changes require Wave review. Not automatically updated.</span>' : ''}
              ${doc.role_integrity_review_required ? '<span role="status">Signer role identities require independent review. No invitations may be sent.</span>' : ''}
              ${showSend ? `
                <div class="aa-signwell-live__confirmation">
                  <p><strong>Confirm these SignWell recipients before sending:</strong></p>
                  ${(sendReview.recipients || []).map(r => '<p>' +
                    this.escapeHtml(r.name + ' · ' + r.email) + '</p>').join('')}
                  <label>Type the client business name exactly to authorize sending
                    <input type="text" autocomplete="off"
                      data-aa-signwell-send-name="${id}"
                      placeholder="${this.escapeHtml(sendReview.clientName)}"></label>
                  <div class="aa-signwell-drafts__actions">
                    <button type="button" class="aa-button aa-button--primary"
                      data-aa-signwell-live-action="send" data-aa-signwell-live-id="${id}"
                      ${!sendReview.sendingEnabled || busy ? 'disabled' : ''}>Confirm & Send for Signature</button>
                    <button type="button" class="aa-button aa-button--secondary"
                      data-aa-signwell-live-action="cancel-send" data-aa-signwell-live-id="${id}">Cancel</button>
                  </div>
                  ${!sendReview.sendingEnabled ? '<p>Real sending is still disabled until internal testing is signed off.</p>' : ''}
                </div>` : ''}
            </div>
            <div class="aa-signwell-drafts__actions">
              ${isReviewable ? '<button type="button" class="aa-button aa-button--secondary" data-aa-signwell-live-action="review-link" data-aa-signwell-live-id="' + id + '"' + (busy ? ' disabled' : '') + '>Review in SignWell</button>' : ''}
              ${doc.status === 'prepared' ? '<button type="button" class="aa-button aa-button--secondary" data-aa-signwell-live-action="request-approval" data-aa-signwell-live-id="' + id + '"' + (busy ? ' disabled' : '') + '>Request Approval</button>' : ''}
              ${doc.status === 'awaiting_approval' ? '<button type="button" class="aa-button aa-button--primary" data-aa-signwell-live-action="approve" data-aa-signwell-live-id="' + id + '"' + (busy ? ' disabled' : '') + '>Approve Agreement</button>' : ''}
              ${['awaiting_approval','approved'].includes(doc.status) ? '<button type="button" class="aa-button aa-button--secondary" data-aa-signwell-live-action="request-changes" data-aa-signwell-live-id="' + id + '"' + (busy ? ' disabled' : '') + '>Request Changes</button>' : ''}
              ${doc.status === 'approved' && !doc.role_integrity_review_required && !showSend ? '<button type="button" class="aa-button aa-button--primary" data-aa-signwell-live-action="send-preview" data-aa-signwell-live-id="' + id + '"' + (busy ? ' disabled' : '') + '>Send for Signature</button>' : ''}
              ${['sending','send_needs_reconciliation','sent','viewed','partially_signed','fully_signed'].includes(doc.status)
                ? '<button type="button" class="aa-button aa-button--secondary" data-aa-signwell-live-action="sync" data-aa-signwell-live-id="' + id + '"' + (busy ? ' disabled' : '') + '>Refresh Status</button>' : ''}
              ${doc.signed_pdf_available ? '<button type="button" class="aa-button aa-button--secondary" data-aa-signwell-live-action="signed-url" data-aa-signwell-live-id="' + id + '"' + (busy ? ' disabled' : '') + '>Open Signed PDF</button>' : ''}
            </div>
          </div>`;
        }).join('') + '</div>'
          : this.signwellLiveInitialLoading
            ? '<p class="aa-signwell-drafts__empty" role="status">Loading live agreement records...</p>'
            : '<p class="aa-signwell-drafts__empty">No live agreements prepared yet. Existing test drafts are separate and unaffected.</p>'}
      </section>
    `;
  }

  renderSignwellTemplateFieldMap(template, verified) {
    if (!verified?.verified) return '';
    if (template.type === 'addendum') {
      const expected = this.addendumRequiredApiIds();
      const ids = (verified.fillableFields || []).map(field =>
        String(field.apiId || '').trim().toLowerCase());
      const connected = expected.filter(name => ids.includes(name.toLowerCase()));
      const missing = expected.filter(name => !ids.includes(name.toLowerCase()));
      return `
        <div class="aa-signwell-map aa-signwell-map--automatic" aria-label="Addendum field connections">
          <div class="aa-signwell-map__intro">
            <div>
              <h4>Package Change Addendum | Automatic Fields</h4>
              <p>The addendum has its own field connections, separate from Annual Agreements.</p>
            </div>
            <span class="aa-signwell-map__count">${connected.length} of ${expected.length} connected</span>
          </div>
          <div class="aa-signwell-auto-status">
            <p>${connected.length ? 'Connected: ' + this.escapeHtml(connected.join(', ')) + '.' :
              'No addendum fields verified yet.'}</p>
            ${missing.length
              ? `<p class="aa-signwell-auto-status__review"><strong>Needs review:</strong>
                 ${this.escapeHtml(missing.join(', '))}.</p>`
              : '<p>All thirteen named Addendum fields are connected.</p>'}
          </div>
        </div>
      `;
    }
    const mapping = verified.configuredMapping || template.configuredMapping || {};
    const expected = [
      ['legalBusinessName', 'Client business name'],
      ['businessAddress', 'Client address'],
      ['stateOfOrganization', 'Client state of organization'],
      ['effectiveDate', 'Effective date'],
      ['expirationDate', 'Expiration date'],
      ['servicesSummary', 'Services & frequency'],
      ['packageTotal', 'Total package price']
    ].filter(([key]) => template.type === 'service_agreement' ||
      !['expirationDate','stateOfOrganization'].includes(key));
    const idsFor = (key) =>
      Array.isArray(mapping[key]) ? mapping[key] : mapping[key] ? [mapping[key]] : [];
    const completed = expected.filter(([key]) => idsFor(key).length);
    const outstanding = expected.filter(([key]) => !idsFor(key).length);
    return `
      <div class="aa-signwell-map aa-signwell-map--automatic" aria-label="Automatic agreement connections">
        <div class="aa-signwell-map__intro">
          <div>
            <h4>${this.escapeHtml(template.label)} | Automatic Fields</h4>
            <p>Headquarters matches the document fields automatically. You don't need to choose API IDs.</p>
          </div>
          <span class="aa-signwell-map__count">
            ${completed.length} of ${expected.length} connected
          </span>
        </div>
        <div class="aa-signwell-auto-status">
          ${completed.length
            ? `<p><strong>Connected:</strong> ${completed.map(([,label]) =>
                this.escapeHtml(label)
              ).join(', ')}.</p>`
            : '<p>No contract detail fields could be matched confidently yet.</p>'}
          ${idsFor('legalBusinessName').length > 1
            ? `<p>Client business name will fill in ${idsFor('legalBusinessName').length} locations.</p>`
            : ''}
          ${outstanding.length
            ? `<p class="aa-signwell-auto-status__review"><strong>Template review still needed:</strong>
                 ${outstanding.map(([,label]) => this.escapeHtml(label)).join(', ')}.
                 Nothing will be guessed or filled into an unrelated field.</p>`
            : '<p>All supported agreement details are connected.</p>'}
        </div>
      </div>
    `;
  }


  renderContractsAndQuotes() {
    const quoteCounts =
      this.quotes.reduce(
        (counts, quote) => {
          const status =
            String(
              quote.status ||
              'draft'
            );

          counts[status] =
            Number(
              counts[status] || 0
            ) + 1;

          return counts;
        },
        {}
      );

    const activeAgreements =
      this.contracts.filter(
        (contract) =>
          String(
            contract.status || ''
          ).toLowerCase() ===
          'active'
      ).length;

    return `
      <section class="aa-contract-quote-center">
        <div class="aa-contract-quote-center__header">
          <div>
            <p class="aa-card__label">
              Contract & Quote Center
            </p>
            <h2>
              Agreements & Quotes
            </h2>
            <p>
              Prepare an agreement, manage drafts, or build a client quote.
            </p>
          </div>
          <div class="aa-signwell-drafts__header-actions">
            <button type="button"
              class="aa-button aa-button--primary"
              data-aa-signwell-open-test-draft
              ${this.signwellExhibitStatus.state === 'checking' ? 'disabled' : ''}>
              Prepare Agreement
            </button>
            <button type="button"
              class="aa-button aa-button--secondary"
              data-aa-new-quote>
              New Quote
            </button>
          </div>
        </div>



        ${this.renderSignwellDraftForm()}
        ${this.renderSignwellReviewNextStep()}
        ${this.renderSignwellDraftRows()}
        ${this.renderSignwellLiveWorkflow()}

        <div class="aa-contract-quote-stats">
          ${this.renderSummaryCard(
            'Draft Quotes',
            Number(
              quoteCounts.draft || 0
            )
          )}

          ${this.renderSummaryCard(
            'Sent / Viewed',
            Number(
              quoteCounts.sent || 0
            ) +
            Number(
              quoteCounts.viewed || 0
            )
          )}

          ${this.renderSummaryCard(
            'Accepted',
            Number(
              quoteCounts.accepted || 0
            )
          )}

          ${this.renderSummaryCard(
            'Active Agreements',
            activeAgreements
          )}
        </div>


        ${this.quoteBuilderOpen
          ? this.renderQuoteBuilder()
          : ''
        }

        <div class="aa-contract-quote-grid">
          <article class="aa-contract-quote-panel">
            <div class="aa-contract-quote-panel__heading">
              <div>
                <p class="aa-card__label">
                  Quotes
                </p>
                <h3>Quote Pipeline</h3>
              </div>
            </div>

            ${this.quotes.length
              ? `
                <div class="aa-contract-quote-list">
                  ${this.quotes
                    .slice(0, 20)
                    .map(
                      (quote) => `
                        <div class="aa-contract-quote-row">
                          <div>
                            <strong>
                              ${this.escapeHtml(
                                quote.quote_number ||
                                'Quote'
                              )}
                            </strong>
                            <span>
                              ${this.escapeHtml(
                                this.getQuotePartyName(
                                  quote
                                )
                              )}
                            </span>
                          </div>

                          <span>
                            ${this.escapeHtml(
                              String(
                                quote.quote_type ||
                                'custom'
                              )
                                .replaceAll(
                                  '_',
                                  ' '
                                )
                                .replace(
                                  /\b\w/g,
                                  (letter) =>
                                    letter.toUpperCase()
                                )
                            )}
                          </span>

                          <strong>
                            ${this.escapeHtml(
                              this.getQuoteTotal(
                                quote
                              )
                            )}
                          </strong>

                          <b class="aa-contract-quote-status">
                            ${this.escapeHtml(
                              this.formatAttentionStatus(
                                quote.status
                              )
                            )}
                          </b>
                        </div>
                      `
                    )
                    .join('')}
                </div>
              `
              : `
                <div class="aa-operations-state">
                  No quotes yet. Create the first quote here.
                </div>
              `
            }
          </article>

          <article class="aa-contract-quote-panel">
            <div class="aa-contract-quote-panel__heading">
              <div>
                <p class="aa-card__label">
                  Agreements
                </p>
                <h3>Contract Records</h3>
              </div>
            </div>

            ${this.contracts.length
              ? `
                <div class="aa-contract-quote-list">
                  ${this.contracts
                    .slice(0, 20)
                    .map(
                      (contract) => `
                        <div
                          class="aa-contract-quote-row aa-contract-quote-row--agreement"
                        >
                          <a
                            class="aa-contract-quote-main-link"
                            href="${this.escapeHtml(
                              this.getClientAccountHref(
                                contract.client_id,
                                'agreements'
                              )
                            )}"
                          >
                            <strong>
                              ${this.escapeHtml(
                                contract.contract_title ||
                                'Agreement'
                              )}
                            </strong>
                            <span>
                              ${this.escapeHtml(
                                this.getClientName(
                                  contract.client_id
                                )
                              )}
                            </span>
                          </a>

                          <span>
                            ${this.escapeHtml(
                              this.formatAttentionStatus(
                                contract.contract_type
                              )
                            )}
                          </span>

                          <span>
                            ${this.escapeHtml(
                              this.formatDate(
                                contract.effective_date
                              )
                            )}
                          </span>

                          <b class="aa-contract-quote-status">
                            ${this.escapeHtml(
                              this.formatAttentionStatus(
                                contract.status
                              )
                            )}
                          </b>

                          ${String(contract.status || '').toLowerCase() === 'archived'
                            ? '<span class="aa-contract-quote-status">Archived</span>'
                            : (contract.signed_at || ['active','signed','completed','renewal_due','expired'].includes(String(contract.status || '').toLowerCase()))
                              ? `<button type="button" class="aa-contract-quote-delete"
                                    data-aa-contract-archive="${this.escapeHtml(contract.id)}">
                                    Archive
                                  </button>`
                              : `<button type="button" class="aa-contract-quote-delete"
                                    data-aa-contract-delete="${this.escapeHtml(contract.id)}">
                                    Delete
                                  </button>`
                          }
                        </div>
                      `
                    )
                    .join('')}
                </div>
              `
              : `
                <div class="aa-operations-state">
                  No agreement records are stored yet.
                </div>
              `
            }
          </article>
        </div>
        <details class="aa-signwell-settings" ${this.signwellSettingsOpen ? 'open' : ''}>
          <summary data-aa-signwell-settings-toggle>SignWell connection & template settings</summary>
          ${this.signwellMappingMessage
            ? '<p class="aa-signwell-drafts__message" role="status">' + this.escapeHtml(this.signwellMappingMessage) + '</p>'
            : ''}
        <div class="aa-contract-quote-panel" style="margin:20px 0 24px;padding:24px;display:flex;gap:18px;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;">
          <div style="min-width:260px;flex:1;">
            <h3 style="font-size:22px;line-height:1.3;margin:0 0 8px;">SignWell Templates</h3>
            <p style="font-size:16px;line-height:1.5;margin:0 0 12px;">
              Linked to Headquarters. Verify securely before sending agreements.
            </p>
            ${(this.signwellConfiguration?.templates || []).length
              ? this.signwellConfiguration.templates.map((template) => {
                  const verified = this.signwellTest.templates?.find((item) => item.type === template.type);
                  const state = verified
                    ? verified.verified ? 'Verified in SignWell' : verified.message || 'Not verified'
                    : template.configured ? 'Linked · awaiting verification' : 'Not configured';
                  return `<div class="aa-signwell-template-section">
                    <strong class="aa-signwell-template-section__title">${this.escapeHtml(template.label)}</strong>
                    <span style="display:block;">${this.escapeHtml(state)}</span>
                    ${verified?.verified && Array.isArray(verified.signerRoles)
                      ? `<div style="margin-top:10px;padding:12px 14px;background:var(--color-background, #f7f7f7);border-radius:8px;">
                          <strong style="display:block;margin-bottom:8px;">Signature roles (${verified.signerRoles.length})</strong>
                          ${verified.signerRoles.map((role, i) =>
                            `<div style="margin:8px 0;line-height:1.5;">
                              ${i + 1}. <strong>${this.escapeHtml(role.placeholderName || 'Unnamed signer')}</strong>
                              · ${role.hasRequiredSignature ? 'Required signature found' : 'Required signature needs review'}
                              ${role.preassigned ? ' · Preassigned recipient' : ''}
                            </div>`
                          ).join('')}
                          ${verified.signerRoles.length !== 3
                            ? '<p style="margin:8px 0 0;">Expected three signing roles: client, Tiffany and Whitney.</p>'
                            : ''}
                        </div>`
                      : ''}
                    ${this.renderSignwellTemplateFieldMap(template,verified)}
                  </div>`;
                }).join('')
              : '<p style="font-size:16px;">Unable to load template settings.</p>'
            }
            <p role="status" aria-live="polite" style="font-size:16px;margin:12px 0 0;">
              ${this.escapeHtml(
                this.signwellTest.state === 'connected'
                  ? 'SignWell connected. Confirm signer roles before enabling document sending.'
                  : this.signwellTest.state === 'partial'
                    ? 'Connection verified. Review the template status above.'
                    : this.signwellTest.state === 'checking'
                      ? 'Checking SignWell and both templates...'
                      : this.signwellTest.message || 'No documents will be sent by this verification.'
              )}
            </p>
          </div>
          <button type="button" class="aa-button aa-button--secondary"
            data-aa-signwell-test
            ${this.signwellTest.state === 'checking' ? 'disabled' : ''}>
            ${this.signwellTest.state === 'checking' ? 'Checking...' : 'Reconnect Annual Agreement Fields'}
          </button>
        </div>
        </details>
      </section>
    `;
  }

  renderPackagesWorkspace() {
    return `
      <section class="aa-contract-quote-center">
        <div class="aa-contract-quote-center__header">
          <div>
            <p class="aa-card__label">
              Monthly Client Packages
            </p>
            <h2>
              Contracted delivery by client
            </h2>
            <p>
              Review what each client is contracted for,
              what is scheduled, and what still remains.
            </p>
          </div>
        </div>

        ${this.renderPackageDelivery()}
      </section>
    `;
  }

  renderActiveView() {
    if (
      this.activeView ===
      'contracts'
    ) {
      if (!this.contractDataLoaded) {
        return `<section class="aa-operations-state">${this.contractDataError
          ? this.escapeHtml(this.contractDataError)
          : 'Loading Contracts & Quotes...'}${this.contractDataError
            ? '<button type="button" class="aa-button aa-button--secondary" data-aa-retry-contracts>Retry</button>'
            : ''}</section>`;
      }
      return this.renderContractsAndQuotes();
    }

    if (
      this.activeView ===
      'packages'
    ) {
      return this.renderPackagesWorkspace();
    }

    return `
      <section class="aa-operations-command-grid">
        ${this.renderBusinessHealth()}
        ${this.renderInterventionCenter()}
      </section>
    `;
  }


  render() {
    this.root.innerHTML = `
      <div class="aa-operations-workspace">
        ${this.loading ? `
          <div class="aa-operations-state">
            Loading Operations...
          </div>
        ` : this.error ? `
          <div class="aa-operations-state aa-operations-state--error">
            ${this.escapeHtml(
              this.error
            )}
          </div>
        ` : `
          ${this.renderOperationsTabs()}
          ${this.renderActiveView()}
        `}
      </div>
    `;
  }

  bindEvents() {
    this.root.addEventListener(
      'submit',
      async (event) => {
        const signwellDraftForm = event.target.closest('[data-aa-signwell-draft-form]');
        if (signwellDraftForm) {
          event.preventDefault();
          if (this.signwellDraftBusy) return;
          const fd = new FormData(signwellDraftForm);
          const fields = {};
          for (const key of Object.keys(this.signwellDraftFields)) {
            fields[key] = String(fd.get(key) || '').trim();
          }
          this.signwellDraftFields = fields;
          const effective = String(fields.effectiveDate || '');
          const expiration = String(fields.expirationDate || '');
          const stateOfOrganization = String(fields.stateOfOrganization || '').trim();
          const packagePrice = Number(fields.packagePrice);
          const managementFee = String(fields.managementFee ?? '0.00').trim();
          const oneTimeFee = String(fields.oneTimeFee ?? '0.00').trim();
          if (!/^\d+(?:\.\d{1,2})?$/.test(managementFee) ||
              !Number.isFinite(Number(managementFee)) || Number(managementFee) < 0 ||
              Number(managementFee) > 10000000 ||
              (fields.templateType === 'addendum' &&
                (!/^\d+(?:\.\d{1,2})?$/.test(oneTimeFee) ||
                 !Number.isFinite(Number(oneTimeFee)) || Number(oneTimeFee) < 0 ||
                 Number(oneTimeFee) > 10000000))) {
            this.signwellDraftMessage = 'Enter a valid Management Fee, or leave 0.00 when none applies.';
            this.render();
            return;
          }
          if (!fields.packagePrice || !Number.isFinite(packagePrice) ||
              packagePrice < 0 || packagePrice > 10000000 ||
              !/^\d+(?:\.\d{1,2})?$/.test(fields.packagePrice) ||
              !['per month','per quarter','per year','per week','one time'].includes(fields.billingPeriod) ||
              (fields.templateType === 'addendum' && fields.billingPeriod !== 'per month')) {
            this.signwellDraftMessage = 'Enter one valid total package price and its billing frequency.';
            this.render();
            return;
          }
          if (!String(fields.legalBusinessName || '').trim() ||
              !String(fields.businessAddress || '').trim() ||
              !stateOfOrganization ||
              !/^\d{4}-\d{2}-\d{2}$/.test(effective) ||
              (fields.templateType === 'service_agreement' &&
                (!/^\d{4}-\d{2}-\d{2}$/.test(expiration) || expiration < effective))) {
            this.signwellDraftMessage = 'Confirm the business name, address and agreement dates before creating a test draft.';
            this.render();
            return;
          }
          if (fields.templateType === 'addendum' &&
              (!/^\d{4}-\d{2}-\d{2}$/.test(fields.originalAgreementDate || '') ||
               fields.originalAgreementDate > effective ||
               !String(fields.addendumChange1 || '').trim() ||
               [1,2,3].some(number =>
                 String(fields['addendumChange'+number] || '').length > 190))) {
            this.signwellDraftMessage = 'Enter the original agreement date and describe the specific package change.';
            this.render();
            this.root.querySelector('[data-aa-signwell-draft-form]')?.scrollIntoView?.({block:'start'});
            return;
          }
          // Stop locally before calling SignWell if the template would leave
          // business details blank or assigned to the client signer.
          const currentTemplate = this.signwellConfiguration?.templates?.find(
            template => template.type === fields.templateType
          );
          const mapped = currentTemplate?.configuredMapping || {};
          if (fields.templateType === 'addendum') {
            const fieldNames = this.addendumRequiredApiIds();
            const verified = this.signwellTest.templates?.find(item => item.type === 'addendum');
            const foundIds = (verified?.fillableFields || []).map(item =>
              String(item.apiId || '').trim().toLowerCase());
            const missing = fieldNames.filter(name => !foundIds.includes(name.toLowerCase()));
            if (!verified?.verified || missing.length) {
              this.signwellDraftMessage = !verified?.verified
                ? 'Could not verify the latest addendum template. Select Check Addendum Fields and try again.'
                : 'Addendum field connection needs attention: ' + missing.join(', ') +
                  '. No draft created.';
              this.render();
              this.root.querySelector('[data-aa-signwell-draft-form]')?.scrollIntoView?.({block:'start'});
              return;
            }
          } else {
            const required = ['legalBusinessName','businessAddress','stateOfOrganization',
              'effectiveDate','expirationDate','servicesSummary','packageTotal'];
            const pending = required.filter(key =>
              Array.isArray(mapped[key]) ? mapped[key].length === 0 : !mapped[key]);
            if (pending.length) {
              this.signwellDraftMessage = 'Annual Agreement fields need attention: ' +
                pending.join(', ') + '. No draft created.';
              this.render();
              return;
            }
          }
          this.syncSignwellDraftServicesFromDom(signwellDraftForm);
          const schedule = this.signwellDraftServices.map(row => ({
            serviceCatalogId: row.serviceCatalogId,
            quantity: Number(row.quantity),
            frequency: row.frequency
          }));
          if (this.signwellServicesBusy || !schedule.length ||
              schedule.some(row => !row.serviceCatalogId || !Number.isInteger(row.quantity) || row.quantity < 1) ||
              new Set(schedule.map(row => row.serviceCatalogId)).size !== schedule.length) {
            this.signwellDraftMessage = 'Choose at least one valid service. Each service can appear only once.';
            this.render();
            return;
          }
          this.signwellDraftBusy = true;
          this.signwellDraftMessage = '';
          this.render();
          try {
            const livePreparation = this.signwellPreparationMode === 'live';
            if (livePreparation && !this.signwellLivePreparationEnabled) {
              throw new Error('Unsigned live preparation is not enabled.');
            }
            const result = await (livePreparation
              ? window.AASupabaseClient.prepareSignwellLiveDraft
              : window.AASupabaseClient.prepareSignwellTestDraft)({
              packageChangeRequestId:fields.packageChangeRequestId,
              clientId: fields.clientId,
              templateType: fields.templateType,
              serviceSchedule: schedule,
              agreementDetails: {
                legalBusinessName: fields.legalBusinessName,
                businessAddress: fields.businessAddress,
                stateOfOrganization: fields.stateOfOrganization,
                effectiveDate: fields.effectiveDate,
                expirationDate: fields.templateType === 'service_agreement' ? fields.expirationDate : '',
                packagePrice: fields.packagePrice,
                managementFee,
                oneTimeFee: fields.templateType === 'addendum' ? oneTimeFee : '0.00',
                originalAgreementDate: fields.templateType === 'addendum' ? fields.originalAgreementDate : '',
                addendumChange1: fields.templateType === 'addendum' ? fields.addendumChange1 : '',
                addendumChange2: fields.templateType === 'addendum' ? fields.addendumChange2 : '',
                addendumChange3: fields.templateType === 'addendum' ? fields.addendumChange3 : '',
                billingPeriod: fields.billingPeriod
              },
              recipients: {
                client: { name: fields.clientName, email: fields.clientEmail },
                owner1: { name: fields.owner1Name, email: fields.owner1Email },
                owner2: { name: fields.owner2Name, email: fields.owner2Email }
              }
            });
            if (!result?.document?.id) {
              throw new Error('SignWell created a draft but Headquarters did not receive its tracking reference. Do not retry until this is reconciled.');
            }
            const count = Number(result.prefilledFieldCount || 0);
            const prepared = {
              ...result.document,
              prefilledFieldCount: Number.isFinite(count) ? count : 0
            };
            if (livePreparation) {
              this.signwellLiveMessage = 'Unsigned live agreement prepared. Review it and request approval before sending.';
              await this.refreshSignwellLiveAgreements();
            } else {
              const alreadyListed = this.signwellDrafts.some(draft =>
                String(draft.id) === String(prepared.id));
              this.signwellDrafts = [
                prepared, ...this.signwellDrafts.filter(draft =>
                  String(draft.id) !== String(prepared.id))
              ].filter(Boolean).slice(0,200);
              if (!alreadyListed) this.signwellDraftTotal += 1;
              this.signwellLastPreparedId = prepared.id;
              this.signwellLastPreparedCount = prepared.prefilledFieldCount;
            }
            this.signwellDraftOpen = false;
            this.signwellDraftMessage = '';
          } catch (error) {
            this.signwellDraftMessage = error?.message || 'Unable to prepare the test draft.';
          } finally {
            this.signwellDraftBusy = false;
            this.render();
            if (this.signwellPreparationMode === 'live' && !this.signwellDraftOpen) {
              this.root.querySelector('.aa-signwell-live')
                ?.scrollIntoView?.({behavior:'smooth',block:'start'});
            } else if (this.signwellLastPreparedId && !this.signwellDraftOpen) {
              this.root.querySelector('[data-aa-signwell-ready-to-review]')
                ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            } else if (this.signwellDraftMessage && this.signwellDraftOpen) {
              this.root.querySelector('[data-aa-signwell-draft-form]')
                ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            }
          }
          return;
        }

        const form =
          event.target.closest(
            '[data-aa-quote-builder]'
          );

        if (!form) {
          return;
        }

        event.preventDefault();

        this.syncQuoteItemsFromDom();

        if (
          !this.quoteDraftItems.length
        ) {
          this.quoteMessage =
            'Add at least one service.';
          this.render();
          return;
        }

        this.quoteSaving = true;
        this.quoteMessage = '';
        this.render();

        try {
          const formData =
            new FormData(form);

          let prospectId = null;

          if (
            this.quoteAudience ===
            'prospect'
          ) {
            const prospect =
              await window.AASupabaseClient
                .createProspect({
                  businessName:
                    formData.get(
                      'prospectBusiness'
                    ),
                  contactName:
                    formData.get(
                      'recipientName'
                    ),
                  email:
                    formData.get(
                      'recipientEmail'
                    ),
                  phone:
                    formData.get(
                      'recipientPhone'
                    )
                });

            prospectId =
              prospect.id;
          }

          const today =
            new Date()
              .toISOString()
              .slice(0, 10);

          await window.AASupabaseClient
            .createQuote({
              audienceType:
                this.quoteAudience,
              clientId:
                formData.get(
                  'clientId'
                ) || null,
              prospectId,
              quoteType:
                this.quoteType,
              title:
                formData.get(
                  'title'
                ),
              issuedDate: today,
              validThrough:
                formData.get(
                  'validThrough'
                ) || null,
              senderTeamMemberId:
                formData.get(
                  'senderTeamMemberId'
                ) || null,
              recipientName:
                formData.get(
                  'recipientName'
                ),
              recipientEmail:
                formData.get(
                  'recipientEmail'
                ),
              recipientPhone:
                formData.get(
                  'recipientPhone'
                ),
              introText:
                formData.get(
                  'introText'
                ),
              items:
                this.quoteDraftItems
            });

          this.quoteBuilderOpen = false;
          this.quoteDraftItems = [];
          this.quoteMessage = '';

          await this.load();
          this.activeView =
            'contracts';
          this.render();
        } catch (error) {
          console.error(
            'Unable to save quote:',
            error
          );

          this.quoteMessage =
            error?.message ||
            'Unable to save quote.';
        } finally {
          this.quoteSaving = false;
          this.render();
        }
      }
    );

    this.root.addEventListener(
      'input',
      (event) => {
        const form = event.target.closest('[data-aa-signwell-draft-form]');
        if (form) {
          // When the business address changes, suggest its state only if the
          // current field is empty or still contains the previous suggestion.
          // Do not overwrite an independently entered legal formation state.
          if (event.target.matches('[name="businessAddress"]')) {
            const stateField = form.elements.namedItem('stateOfOrganization');
            if (stateField) {
              const previousSuggestion = String(form.dataset.aaSuggestedState || '');
              const nextSuggestion = this.getAgreementStateFromAddress(event.target.value);
              const current = String(stateField.value || '').trim();
              if (!current || current.toLowerCase() === previousSuggestion.toLowerCase()) {
                stateField.value = nextSuggestion;
              }
              form.dataset.aaSuggestedState = nextSuggestion;
            }
          }
          this.syncSignwellDraftFields(form);
          this.syncSignwellDraftServicesFromDom(form);
          this.syncSignwellLibraryValues(form);
          // Old API failures are no longer current once the draft changes.
          // Clear the stale error in place without re-rendering or losing edits.
          if (this.signwellDraftMessage) {
            this.signwellDraftMessage = '';
            form.querySelector('.aa-signwell-draft__notice[aria-live="polite"]')?.remove();
          }
        }
      }
    );

    this.root.addEventListener(
      'change',
      async (event) => {
        const signwellClientSelect = event.target.closest(
          '[data-aa-signwell-draft-form] select[name="clientId"]'
        );
        if (signwellClientSelect) {
          const form = signwellClientSelect.closest('[data-aa-signwell-draft-form]');
          this.syncSignwellDraftFields(form);
          const signer = this.getSignwellClientSigner(signwellClientSelect.value);
          this.signwellDraftFields.clientId = signwellClientSelect.value;
          this.signwellDraftFields.clientName = signer.name;
          this.signwellDraftFields.clientEmail = signer.email;
          const business = this.getSignwellClientBusinessDetails(signwellClientSelect.value);
          this.signwellDraftFields.legalBusinessName = business.legalBusinessName;
          this.signwellDraftFields.businessAddress = business.businessAddress;
          this.signwellDraftFields.stateOfOrganization = business.stateOfOrganization;
          // Never carry a package total across different clients.
          this.signwellDraftFields.packagePrice = '';
          this.signwellDraftFields.managementFee = '0.00';
          this.signwellDraftFields.oneTimeFee = '0.00';
          this.signwellDraftFields.originalAgreementDate = '';
          this.signwellDraftFields.addendumChange1 = '';
          this.signwellDraftFields.addendumChange2 = '';
          this.signwellDraftFields.addendumChange3 = '';
          this.signwellDraftFields.billingPeriod = 'per month';
          // Dates remain manual. The suggested state was loaded from this client's address.
          this.signwellDraftFields.effectiveDate = '';
          this.signwellDraftFields.expirationDate = '';
          this.signwellDraftMessage = '';
          this.signwellDraftServices = [];
          this.render();
          await this.loadSignwellClientPackage(signwellClientSelect.value);
          return;
        }

        const agreementTypeField = event.target.closest(
          '[data-aa-signwell-draft-form] select[name="templateType"]'
        );
        if (agreementTypeField) {
          const form = agreementTypeField.closest('[data-aa-signwell-draft-form]');
          this.syncSignwellDraftFields(form);
          this.syncSignwellDraftServicesFromDom(form);
          this.syncSignwellLibraryValues(form);
          this.signwellDraftMessage = '';
          if (this.signwellDraftFields.templateType === 'addendum') {
            this.signwellAddendumStatus = {state:'unchecked',message:''};
            this.render();
            await this.refreshAddendumConnection();
          }
          this.render();
          return;
        }

        const changedAgreementService = event.target.closest('[data-aa-agreement-service-id]');
        if (changedAgreementService) {
          const form = changedAgreementService.closest('[data-aa-signwell-draft-form]');
          this.syncSignwellDraftFields(form);
          this.syncSignwellDraftServicesFromDom(form);
          const allRows = Array.from(form.querySelectorAll('[data-aa-agreement-service-row]'));
          const index = allRows.indexOf(changedAgreementService.closest('[data-aa-agreement-service-row]'));
          const chosen = this.signwellServiceCatalog.find(
            row => String(row.id) === String(changedAgreementService.value)
          );
          if (chosen && index >= 0) this.signwellDraftServices[index].frequency =
            this.normalizeAgreementFrequency(chosen.defaultUnitLabel);
          this.render();
          return;
        }

        const audience =
          event.target.closest(
            '[data-aa-quote-audience]'
          );

        if (audience) {
          this.syncQuoteItemsFromDom();
          this.quoteAudience =
            audience.value ||
            'client';
          this.render();
          return;
        }

        const quoteType =
          event.target.closest(
            '[data-aa-quote-type]'
          );

        if (quoteType) {
          this.syncQuoteItemsFromDom();
          this.quoteType =
            quoteType.value ||
            'custom';
          this.render();
          return;
        }

        const serviceSelect =
          event.target.closest(
            '[data-aa-quote-service-select]'
          );

        if (serviceSelect) {
          this.syncQuoteItemsFromDom();

          const row =
            serviceSelect.closest(
              '[data-aa-quote-item]'
            );

          const index =
            Number(
              row?.dataset
                .aaQuoteItemIndex
            );

          const service =
            this.quoteServices.find(
              (item) =>
                String(item.id) ===
                String(
                  serviceSelect.value
                )
            );

          if (
            Number.isInteger(index) &&
            this.quoteDraftItems[index]
          ) {
            this.quoteDraftItems[index] =
              service
                ? this.makeQuoteItem(
                    service
                  )
                : {
                    ...this.quoteDraftItems[
                      index
                    ],
                    serviceCatalogId: ''
                  };
          }

          this.render();
          return;
        }

        const filter =
          event.target.closest(
            '[data-aa-operations-filter]'
          );

        if (!filter) {
          return;
        }

        if (
          filter.closest(
            '[data-aa-package-delivery-details]'
          )
        ) {
          this.packageDeliveryOpen = true;
        }

        const name =
          filter.dataset
            .aaOperationsFilter;

        const value =
          filter.value || 'all';

        if (name === 'month') {
          this.month =
            value ||
            this.getCurrentMonthValue();

          this.load();
          return;
        }

        if (name === 'client') {
          this.filterClientId =
            value;
        }

        if (name === 'owner') {
          this.filterOwner =
            value;
        }

        if (name === 'service') {
          this.filterService =
            value;
        }

        this.render();
      }
    );

    this.root.addEventListener(
      'click',
      async (event) => {
        const agreementForm = this.root.querySelector('[data-aa-signwell-draft-form]');
        const preserveAgreementEditor = () => {
          if (agreementForm) {
            this.syncSignwellDraftFields(agreementForm);
            this.syncSignwellDraftServicesFromDom(agreementForm);
            this.syncSignwellLibraryValues(agreementForm);
          }
        };

        if (event.target.closest('[data-aa-signwell-refresh-live]')) {
          preserveAgreementEditor();
          await this.refreshSignwellLiveAgreements(false, true);
          return;
        }
        if (event.target.closest('[data-aa-signwell-run-reconciliation]')) {
          preserveAgreementEditor();
          await this.refreshSignwellLiveAgreements(true, true);
          return;
        }
        if (event.target.closest('[data-aa-signwell-use-qa-client]')) {
          if (this.signwellPreparationMode !== 'test') return;
          const qaEmail = String(this.signwellConfiguration?.qaClientEmail || '').trim();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(qaEmail)) {
            this.signwellDraftMessage = 'The internal test email is not configured.';
            this.render();
            return;
          }
          preserveAgreementEditor();
          this.signwellDraftFields.clientName = 'Internal Test Client';
          this.signwellDraftFields.clientEmail = qaEmail;
          this.signwellDraftMessage = 'Internal test signer selected. No invitations will be sent when you prepare the draft.';
          this.render();
          return;
        }
        const prepModeButton = event.target.closest('[data-aa-signwell-prepare-mode]');
        if (prepModeButton) {
          preserveAgreementEditor();
          const mode = String(prepModeButton.dataset.aaSignwellPrepareMode || '');
          if (!['test','live'].includes(mode)) return;
          if (mode === 'live' && !this.signwellLivePreparationEnabled) return;
          this.signwellPreparationMode = mode;
          this.signwellDraftMessage = '';
          this.render();
          return;
        }
        const liveButton = event.target.closest('[data-aa-signwell-live-action]');
        if (liveButton) {
          preserveAgreementEditor();
          const action = String(liveButton.dataset.aaSignwellLiveAction || '');
          const id = String(liveButton.dataset.aaSignwellLiveId || '');
          if (!id || this.signwellLiveBusyId) return;
          if (action === 'cancel-send') {
            this.signwellLiveSendReview = null;
            this.render();
            return;
          }
          if (action === 'approve' && !window.confirm(
            'I reviewed the final SignWell draft and verified all three required signature fields, plus the client signer name and title fields. Approve this agreement?'
          )) return;
          if (action === 'send') {
            const entered = String(this.root.querySelector(
              '[data-aa-signwell-send-name="' + id + '"]')?.value || '').trim();
            if (!this.signwellLiveSendReview ||
                this.signwellLiveSendReview.id !== id ||
                entered !== this.signwellLiveSendReview.clientName) {
              this.signwellLiveMessage = 'Type the exact client business name before sending.';
              this.render();
              return;
            }
            if (!window.confirm(
              'Send this approved agreement through SignWell to all three recipients? This sends real signature invitations.'
            )) return;
          }
          const newTab = ['review-link','signed-url'].includes(action)
            ? window.open('about:blank','_blank') : null;
          if (['review-link','signed-url'].includes(action) && !newTab) {
            this.signwellLiveMessage = 'Allow popups to open the SignWell document.';
            this.render();
            return;
          }
          if (newTab) newTab.opener = null;
          const confirmation = action === 'send'
            ? String(this.root.querySelector('[data-aa-signwell-send-name="' + id + '"]')?.value || '').trim()
            : '';
          this.signwellLiveBusyId = id;
          this.signwellLiveMessage = '';
          this.render();
          try {
            const extra = action === 'approve'
              ? {signatureFieldsVerified:true}
              : action === 'send' ? {confirmation} : {};
            const result = await window.AASupabaseClient.runSignwellLiveAction(action,id,extra);
            if (newTab) {
              if (!result?.url) throw new Error('The document link could not be opened.');
              newTab.location.replace(result.url);
            } else if (action === 'send-preview') {
              this.signwellLiveSendReview = {...result,id};
            } else {
              this.signwellLiveSendReview = null;
              // Only the confirmed durable send-finalization response can
              // produce success messaging. Uncertain results must use error UI.
              if (action === 'send' && result?.status !== 'sent')
                throw new Error('Sending is not confirmed. Refresh Status before taking further action.');
              this.signwellLiveMessage = action === 'send'
                ? 'SignWell invitations sent and recorded.'
                : action === 'approve' ? 'Agreement approved. Sending still requires separate confirmation.'
                : action === 'request-approval' ? 'Agreement is awaiting approval.'
                : action === 'sync' ? 'SignWell status refreshed.'
                : action === 'request-changes' ? 'Returned to preparation for changes.'
                : 'Agreement updated.';
              await this.refreshSignwellLiveAgreements();
            }
          } catch (error) {
            if (newTab) try { newTab.close(); } catch (_) {}
            this.signwellLiveMessage = action === 'send'
              ? (error?.message || 'Sending is uncertain. Do not send again. Use Refresh Status.')
              : (error?.message || 'Unable to complete agreement action.');
          } finally {
            this.signwellLiveBusyId = '';
            this.render();
          }
          return;
        }

        if (event.target.closest('[data-aa-agreement-add-service]')) {
          preserveAgreementEditor();
          const selectedIds = new Set(this.signwellDraftServices.map(row => String(row.serviceCatalogId)));
          const nextService = this.signwellServiceCatalog.find(row => !selectedIds.has(String(row.id)));
          if (nextService) {
            this.signwellDraftServices.push({
              serviceCatalogId: nextService.id,
              quantity: 1,
              frequency: this.normalizeAgreementFrequency(nextService.defaultUnitLabel)
            });
            this.signwellServicesMessage = '';
          } else {
            this.signwellServicesMessage = 'All available services are already selected.';
          }
          this.render();
          return;
        }

        const agreementRemoveButton = event.target.closest('[data-aa-agreement-remove-service]');
        if (agreementRemoveButton) {
          preserveAgreementEditor();
          const index = Number(agreementRemoveButton.dataset.aaAgreementRemoveService);
          if (Number.isInteger(index) && index >= 0) this.signwellDraftServices.splice(index, 1);
          this.signwellServicesMessage = '';
          this.render();
          return;
        }

        if (event.target.closest('[data-aa-signwell-library-toggle]')) {
          preserveAgreementEditor();
          this.signwellLibraryOpen = !this.signwellLibraryOpen;
          this.signwellLibraryMessage = '';
          this.render();
          if (this.signwellLibraryOpen) {
            this.root.querySelector('[data-aa-signwell-library]')
              ?.scrollIntoView?.({behavior:'smooth',block:'start'});
            this.root.querySelector('[data-aa-signwell-library-name]')
              ?.focus?.({preventScroll:true});
          } else {
            this.root.querySelector('[data-aa-signwell-library-toggle]')
              ?.scrollIntoView?.({behavior:'smooth',block:'nearest'});
          }
          return;
        }

        if (event.target.closest('[data-aa-signwell-library-add-new]')) {
          preserveAgreementEditor();
          this.signwellLibraryOpen = true;
          this.signwellLibraryEditId = '';
          this.signwellLibraryValues = {
            name:'', category:'Marketing', frequency:'per month', description:''
          };
          this.signwellLibraryMessage = '';
          this.render();
          this.root.querySelector('[data-aa-signwell-library]')
            ?.scrollIntoView?.({behavior:'smooth',block:'start'});
          this.root.querySelector('[data-aa-signwell-library-name]')
            ?.focus?.({preventScroll:true});
          return;
        }

        if (event.target.closest('[data-aa-signwell-library-close]')) {
          preserveAgreementEditor();
          this.signwellLibraryOpen = false;
          this.signwellLibraryEditId = '';
          this.render();
          this.root.querySelector('[data-aa-signwell-library-toggle]')
            ?.scrollIntoView?.({behavior:'smooth',block:'nearest'});
          return;
        }
        if (event.target.closest('[data-aa-signwell-library-reset]')) {
          preserveAgreementEditor();
          this.signwellLibraryEditId = '';
          this.signwellLibraryValues = {
            name:'', category:'Marketing', frequency:'per month', description:''
          };
          this.signwellLibraryMessage = '';
          this.render();
          return;
        }
        const agreementLibraryEditButton = event.target.closest('[data-aa-signwell-library-edit]');
        if (agreementLibraryEditButton) {
          preserveAgreementEditor();
          const service = this.signwellServiceCatalog.find(
            row => String(row.id) === String(agreementLibraryEditButton.dataset.aaSignwellLibraryEdit)
          );
          if (!service) return;
          this.signwellLibraryEditId = service.id;
          this.signwellLibraryDeleteId = '';
          this.signwellLibraryValues = {
            name: service.name || '',
            category: service.category || 'Marketing',
            frequency: this.normalizeAgreementFrequency(service.defaultUnitLabel),
            description: service.description || ''
          };
          this.signwellLibraryMessage = '';
          this.render();
          return;
        }
        const agreementLibraryDeleteButton = event.target.closest('[data-aa-signwell-library-delete]');
        if (agreementLibraryDeleteButton) {
          preserveAgreementEditor();
          this.signwellLibraryDeleteId = agreementLibraryDeleteButton.dataset.aaSignwellLibraryDelete || '';
          this.signwellLibraryMessage = '';
          this.render();
          return;
        }
        if (event.target.closest('[data-aa-signwell-library-cancel-delete]')) {
          preserveAgreementEditor();
          this.signwellLibraryDeleteId = '';
          this.render();
          return;
        }

        const agreementLibraryConfirmDelete = event.target.closest('[data-aa-signwell-library-confirm-delete]');
        if (agreementLibraryConfirmDelete) {
          preserveAgreementEditor();
          const id = agreementLibraryConfirmDelete.dataset.aaSignwellLibraryConfirmDelete || '';
          if (!id || this.signwellLibraryDeleteId !== id || this.signwellLibraryBusy) return;
          this.signwellLibraryBusy = true;
          this.render();
          try {
            const assigned = await window.AASupabaseClient.getTaskPackageServices();
            if (assigned.some(row => String(row.serviceCatalogId) === String(id))) {
              throw new Error(
                'This service is currently used in a client package. Remove it from active packages before deleting it from the library.'
              );
            }
            const result = await window.AAPlaybookService.archiveServiceCatalogItem({ serviceId: id });
            if (!result?.success) throw result?.error || new Error('Unable to delete service.');
            this.signwellServiceCatalog = await window.AAPlaybookService.getServiceCatalog();
            this.signwellDraftServices = this.signwellDraftServices.filter(row =>
              String(row.serviceCatalogId) !== String(id)
            );
            if (this.signwellLibraryEditId === id) {
              this.signwellLibraryEditId = '';
              this.signwellLibraryValues = {
                name:'', category:'Marketing', frequency:'per month', description:''
              };
            }
            this.signwellLibraryDeleteId = '';
            this.signwellLibraryMessage = 'Service removed from active library. Existing contracts are unchanged.';
          } catch (error) {
            this.signwellLibraryMessage = error?.message || 'Unable to delete service.';
          } finally {
            this.signwellLibraryBusy = false;
            this.render();
          }
          return;
        }

        if (event.target.closest('[data-aa-signwell-library-save]')) {
          preserveAgreementEditor();
          if (this.signwellLibraryBusy) return;
          const values = this.signwellLibraryValues;
          if (!String(values.name || '').trim()) {
            this.signwellLibraryMessage = 'Enter a service name.';
            this.render();
            return;
          }
          this.signwellLibraryBusy = true;
          this.signwellLibraryMessage = '';
          this.render();
          try {
            let savedServiceId = '';
            if (this.signwellLibraryEditId) {
              const response = await window.AASupabaseClient.updateAgreementServiceLibraryItem({
                serviceId: this.signwellLibraryEditId,
                serviceName: values.name,
                category: values.category,
                defaultUnitLabel: values.frequency,
                description: values.description
              });
              savedServiceId = response.serviceId || this.signwellLibraryEditId;
            } else {
              const response = await window.AAPlaybookService.upsertServiceCatalogItem({
                serviceName: values.name,
                category: values.category,
                defaultUnitLabel: values.frequency,
                description: values.description
              });
              if (!response?.success) throw response?.error || new Error('Unable to add service.');
              savedServiceId = response.serviceId;
            }
            this.signwellServiceCatalog = await window.AAPlaybookService.getServiceCatalog();
            if (!this.signwellLibraryEditId && savedServiceId &&
                !this.signwellDraftServices.some(row => String(row.serviceCatalogId) === String(savedServiceId))) {
              this.signwellDraftServices.push({
                serviceCatalogId: savedServiceId,
                quantity: 1,
                frequency: this.normalizeAgreementFrequency(values.frequency)
              });
            }
            this.signwellLibraryEditId = '';
            this.signwellLibraryValues = {
              name:'', category:'Marketing', frequency:'per month', description:''
            };
            this.signwellLibraryMessage = 'Service saved to the reusable Service Library.';
          } catch (error) {
            this.signwellLibraryMessage = error?.message || 'Unable to save service.';
          } finally {
            this.signwellLibraryBusy = false;
            this.render();
          }
          return;
        }

        if (event.target.closest('[data-aa-signwell-open-test-draft]')) {
          if (this.signwellExhibitStatus.state === 'checking') return;
          this.signwellLastPreparedId = '';
          this.signwellDraftMessage = '';
          this.signwellDraftOpen = true;
          this.signwellExhibitStatus = {state:'checking',message:'Verifying Exhibit A in SignWell...'};
          // The form is usable immediately; its Create button remains locked
          // until the exact saved template/Exhibit A connection is verified.
          this.render();
          await this.refreshAnnualExhibitAConnection();
          if (this.signwellDraftFields.templateType === 'addendum') {
            await this.refreshAddendumConnection(true);
          }
          // Do not redraw over input entered while the template was checked.
          if (this.signwellDraftOpen) {
            const form = this.root.querySelector('[data-aa-signwell-draft-form]');
            if (form) {
              this.syncSignwellDraftFields(form);
              this.syncSignwellDraftServicesFromDom(form);
            }
            this.render();
          }
          return;
        }
        if (event.target.closest('[data-aa-signwell-check-addendum]')) {
          preserveAgreementEditor();
          if (this.signwellAddendumStatus.state === 'checking') return;
          await this.refreshAddendumConnection();
          this.render();
          return;
        }
        if (event.target.closest('[data-aa-signwell-check-exhibit]')) {
          if (this.signwellExhibitStatus.state === 'checking') return;
          preserveAgreementEditor();
          this.signwellExhibitStatus = {state:'checking',message:''};
          this.render();
          await this.refreshAnnualExhibitAConnection();
          this.render();
          return;
        }
        if (event.target.closest('[data-aa-signwell-close-test-draft]')) {
          this.signwellDraftOpen = false;
          this.signwellDraftMessage = '';
          this.render();
          return;
        }
        const reviseDraftButton = event.target.closest('[data-aa-signwell-revise-draft]');
        if (reviseDraftButton) {
          if (this.signwellBulkDeleting || this.signwellBusyId) return;
          const id = String(reviseDraftButton.dataset.aaSignwellReviseDraft || '');
          const draft = this.signwellDrafts.find(d => String(d.id) === id);
          if (!draft || draft.mode !== 'test' || draft.status !== 'test_draft' ||
              Number(draft.prefilledFieldCount || 0) < 1) return;
          const details = draft.agreement_details || {};
          const signer = this.getSignwellClientSigner(draft.client_id);
          this.signwellDraftFields = {
            ...this.signwellDraftFields,
            templateType: draft.template_type || 'service_agreement',
            clientId: String(draft.client_id || ''),
            clientName: signer.name || '',
            clientEmail: signer.email || '',
            legalBusinessName: String(details.legalBusinessName || ''),
            businessAddress: String(details.businessAddress || ''),
            stateOfOrganization: String(details.stateOfOrganization || ''),
            effectiveDate: String(details.effectiveDate || ''),
            expirationDate: String(details.expirationDate || ''),
            packagePrice: String(details.packagePrice || ''),
            managementFee: String(details.managementFee ?? '0.00'),
            oneTimeFee: String(details.oneTimeFee ?? '0.00'),
            originalAgreementDate: String(details.originalAgreementDate || ''),
            addendumChange1: String(details.addendumChange1 || ''),
            addendumChange2: String(details.addendumChange2 || ''),
            addendumChange3: String(details.addendumChange3 || ''),
            billingPeriod: String(details.billingPeriod || 'per month')
          };
          this.signwellDraftServices = Array.isArray(draft.service_schedule)
            ? draft.service_schedule.map(service => ({
                serviceCatalogId: String(service.serviceCatalogId || ''),
                quantity: Number(service.quantity || 1),
                frequency: String(service.frequency || 'per month')
              }))
            : [];
          this.signwellServicesMessage = '';
          this.signwellLibraryOpen = false;
          this.signwellLastPreparedId = '';
          this.signwellDraftOpen = false;
          this.signwellExhibitStatus = {state:'checking',message:''};
          this.render();
          if (this.signwellDraftFields.templateType === 'service_agreement') {
            await this.refreshAnnualExhibitAConnection();
          } else {
            await this.refreshAddendumConnection();
          }
          this.signwellDraftOpen = true;
          this.signwellDraftMessage = 'Revising saved details from this draft. Submitting will create a NEW unsigned test draft. It will not change or send the original. Confirm the signer details before creating it.';
          this.render();
          this.root.querySelector('[data-aa-signwell-draft-form]')
            ?.scrollIntoView?.({behavior:'smooth',block:'start'});
          return;
        }
        const openDraft = event.target.closest('[data-aa-signwell-open-draft]');
        if (openDraft) {
          const id = openDraft.dataset.aaSignwellOpenDraft;
          if (!id || this.signwellBusyId) return;
          // Opening synchronously avoids popup blockers after asynchronous verification.
          const tab = window.open('about:blank', '_blank');
          if (!tab) {
            this.signwellDraftActionMessage =
              'Your browser blocked the SignWell tab. Allow popups for Headquarters and try again.';
            this.render();
            return;
          }
          tab.opener = null;
          try {
            tab.document.title = 'Opening SignWell document...';
          } catch (_) {}
          this.signwellBusyId = id;
          this.signwellDraftActionMessage = '';
          this.render();
          try {
            const response = await window.AASupabaseClient.getSignwellTestDraftLink(id);
            if (!response?.url) throw new Error('SignWell did not return an edit link.');
            tab.location.replace(response.url);
          } catch (error) {
            try { tab.close(); } catch (_) {}
            this.signwellDraftActionMessage =
              error?.message || 'Unable to open the SignWell draft.';
          } finally {
            this.signwellBusyId = '';
            this.render();
          }
          return;
        }

        if (event.target.closest('[data-aa-signwell-delete-all-start]')) {
          if (this.signwellBusyId || this.signwellBulkDeleting || !this.signwellDrafts.length ||
              this.signwellDrafts.length !== Number(this.signwellDraftTotal)) return;
          this.signwellBulkDeleteConfirm = true;
          this.signwellDraftActionMessage = '';
          this.render();
          return;
        }

        if (event.target.closest('[data-aa-signwell-delete-all-cancel]')) {
          if (this.signwellBulkDeleting) return;
          this.signwellBulkDeleteConfirm = false;
          this.render();
          return;
        }

        if (event.target.closest('[data-aa-signwell-delete-all-confirm]')) {
          if (!this.signwellBulkDeleteConfirm || this.signwellBusyId || this.signwellBulkDeleting ||
              !this.signwellDrafts.length || this.signwellDrafts.length !== Number(this.signwellDraftTotal)) return;
          // The backend separately verifies each document is an unsigned test
          // draft in SignWell before deleting it. Real/signed records are protected.
          const draftIds = this.signwellDrafts.map(draft => draft.id).filter(Boolean);
          this.signwellBulkDeleting = true;
          this.signwellBulkProgressMessage = 'Deleting 0 of ' + draftIds.length + ' test drafts...';
          this.render();
          let deleted = 0;
          let failed = 0;
          for (const id of draftIds) {
            try {
              await window.AASupabaseClient.deleteSignwellTestDraft(id);
              deleted += 1;
              this.signwellDrafts = this.signwellDrafts.filter(row => String(row.id) !== String(id));
              if (String(this.signwellLastPreparedId) === String(id)) this.signwellLastPreparedId = '';
            } catch (_) {
              failed += 1;
            }
            this.signwellBulkProgressMessage = 'Processed ' + (deleted + failed) +
              ' of ' + draftIds.length + ' test drafts...';
            this.render();
          }
          this.signwellDraftTotal = Math.max(0, this.signwellDraftTotal - deleted);
          // Reconcile partial failures and any concurrent changes with the server.
          try {
            const latest = await window.AASupabaseClient.getSignwellTestDrafts();
            if (!Array.isArray(latest?.drafts)) throw new Error('No draft records returned.');
            this.signwellDrafts = latest.drafts;
            this.signwellDraftTotal = Number(latest?.totalTestDrafts ?? this.signwellDrafts.length);
            this.signwellDraftLoadError = '';
          } catch (error) {
            this.signwellDraftLoadError = error?.message || 'Could not refresh test drafts after deletion.';
            // Keep only confirmed deletions removed from local state.
          }
          this.signwellBulkDeleting = false;
          this.signwellBulkDeleteConfirm = false;
          this.signwellBulkProgressMessage = '';
          this.signwellDraftActionMessage = failed
            ? deleted + ' test drafts deleted. ' + failed +
              ' could not be deleted. Review the remaining drafts; signed documents were not touched.'
            : deleted + ' unsigned test drafts deleted from SignWell and Headquarters.';
          this.render();
          return;
        }

        if (event.target.closest('[data-aa-signwell-refresh-drafts]')) {
          await this.refreshSignwellDrafts();
          return;
        }

        const startDraftDelete = event.target.closest('[data-aa-signwell-start-delete]');
        if (startDraftDelete) {
          if (this.signwellBulkDeleting) return;
          this.signwellConfirmDeleteId = startDraftDelete.dataset.aaSignwellStartDelete || '';
          this.signwellDraftActionMessage = '';
          this.render();
          return;
        }

        if (event.target.closest('[data-aa-signwell-cancel-delete]')) {
          this.signwellConfirmDeleteId = '';
          this.render();
          return;
        }

        const confirmDraftDelete = event.target.closest('[data-aa-signwell-confirm-delete]');
        if (confirmDraftDelete) {
          const id = confirmDraftDelete.dataset.aaSignwellConfirmDelete || '';
          if (!id || id !== this.signwellConfirmDeleteId || this.signwellBusyId) return;
          this.signwellBusyId = id;
          this.signwellDraftActionMessage = '';
          this.render();
          try {
            await window.AASupabaseClient.deleteSignwellTestDraft(id);
            this.signwellDrafts = this.signwellDrafts.filter((row) => String(row.id) !== String(id));
            this.signwellDraftTotal = Math.max(0,this.signwellDraftTotal - 1);
            if (String(this.signwellLastPreparedId) === String(id)) this.signwellLastPreparedId = '';
            this.signwellDraftActionMessage = 'Test draft deleted from SignWell and Headquarters.';
            this.signwellConfirmDeleteId = '';
          } catch (error) {
            this.signwellDraftActionMessage =
              error?.message || 'Unable to delete this test draft.';
          } finally {
            this.signwellBusyId = '';
            this.render();
          }
          return;
        }

        if (event.target.closest('[data-aa-signwell-settings-toggle]')) {
          this.signwellSettingsOpen = !this.signwellSettingsOpen;
          if (this.signwellSettingsOpen &&
              !this.signwellTest.templates?.length &&
              this.signwellTest.state !== 'checking') {
            this.signwellTest = { state: 'checking', message: '', templates: [] };
            this.render();
            try {
              const result = await window.AASupabaseClient.verifySignwellConfiguredTemplates();
              this.signwellTest = {
                state: result.allTemplatesVerified ? 'connected' : 'partial',
                message: '',
                templates: Array.isArray(result.templates) ? result.templates : []
              };
            } catch (error) {
              this.signwellTest = {
                state: 'error',
                message: error?.message || 'Unable to verify SignWell fields.',
                templates: []
              };
            }
            this.render();
            return;
          }
        }

        const savePrefillMapping = event.target.closest('[data-aa-signwell-save-mapping]');
        if (savePrefillMapping) {
          if (this.signwellMappingBusy) return;
          const kind = savePrefillMapping.dataset.aaSignwellSaveMapping || '';
          const form = savePrefillMapping.closest('[data-aa-signwell-map]');
          if (!form || !kind) return;
          const mapping = {};
          form.querySelectorAll('[data-aa-signwell-map-field]').forEach(select => {
            mapping[select.dataset.aaSignwellMapField] = select.value || '';
          });
          this.signwellMappingBusy = true;
          this.signwellSettingsOpen = true;
          this.signwellMappingMessage = '';
          this.render();
          try {
            await window.AASupabaseClient.saveSignwellPrefillMapping({ templateType:kind, mapping });
            const refreshed = await window.AASupabaseClient.verifySignwellConfiguredTemplates();
            this.signwellTest = {
              state: refreshed.allTemplatesVerified ? 'connected' : 'partial',
              message: '',
              templates: Array.isArray(refreshed.templates) ? refreshed.templates : []
            };
            this.signwellMappingMessage = 'Field mapping saved. Your next test draft can pre-fill those fields.';
          } catch (error) {
            this.signwellMappingMessage = error?.message || 'Unable to save the field mapping.';
          } finally {
            this.signwellMappingBusy = false;
            this.render();
          }
          return;
        }

        const signwellTestButton =
          event.target.closest('[data-aa-signwell-test]');

        if (signwellTestButton) {
          if (this.signwellTest.state === 'checking') return;
          this.signwellSettingsOpen = true;
          this.signwellTest = { state: 'checking', message: '' };
          this.render();
          try {
            const result = await window.AASupabaseClient.autoConfigureSignwellPrefill();
            this.signwellTest = {
              state: result.allTemplatesVerified ? 'connected' : 'partial',
              message: '',
              templates: Array.isArray(result.templates) ? result.templates : []
            };
          } catch (error) {
            this.signwellTest = {
              state: 'error',
              message: error?.message || 'SignWell connection could not be verified.',
              templates: []
            };
          }
          this.render();
          return;
        }

        const archiveContractButton = event.target.closest('[data-aa-contract-archive]');
        if (archiveContractButton) {
          event.preventDefault();
          const contractId = archiveContractButton.dataset.aaContractArchive || '';
          if (!contractId) return;
          if (archiveContractButton.dataset.confirmArchive !== 'true') {
            archiveContractButton.dataset.confirmArchive = 'true';
            archiveContractButton.textContent = 'Confirm Archive';
            return;
          }
          archiveContractButton.disabled = true;
          archiveContractButton.textContent = 'Archiving...';
          try {
            const result = await window.AAPlaybookService.archiveContract({ contractId });
            if (!result?.success) throw result?.error || new Error('Unable to archive this agreement.');
            this.contracts = this.contracts.map((contract) =>
              String(contract.id) === String(contractId)
                ? { ...contract, status: 'archived' }
                : contract
            );
            this.render();
          } catch (error) {
            archiveContractButton.disabled = false;
            archiveContractButton.dataset.confirmArchive = 'false';
            archiveContractButton.textContent = error?.message || 'Archive failed';
          }
          return;
        }

        const deleteContract =
          event.target.closest(
            '[data-aa-contract-delete]'
          );

        if (deleteContract) {
          event.preventDefault();
          event.stopPropagation();

          const contractId =
            deleteContract.dataset
              .aaContractDelete || '';

          if (!contractId) {
            return;
          }

          if (
            deleteContract.dataset
              .confirmDelete !== 'true'
          ) {
            deleteContract.dataset
              .confirmDelete = 'true';

            deleteContract.textContent =
              'Confirm Delete';

            window.setTimeout(() => {
              if (
                deleteContract.isConnected &&
                deleteContract.dataset
                  .confirmDelete === 'true'
              ) {
                deleteContract.dataset
                  .confirmDelete = 'false';

                deleteContract.textContent =
                  'Delete';
              }
            }, 5000);

            return;
          }

          deleteContract.disabled = true;
          deleteContract.textContent =
            'Deleting...';

          try {
            const result =
              await window.AAPlaybookService
                .deleteContractPermanently({
                  contractId
                });

            if (!result?.success) {
              throw (
                result?.error ||
                new Error(
                  'Unable to delete contract.'
                )
              );
            }

            this.contracts =
              this.contracts.filter(
                (contract) =>
                  String(contract.id) !==
                  String(contractId)
              );

            this.render();
          } catch (error) {
            console.error(
              'Unable to delete contract:',
              error
            );

            deleteContract.disabled =
              false;

            deleteContract.dataset
              .confirmDelete = 'false';

            deleteContract.textContent =
              error?.message ||
              'Unable to delete';
          }

          return;
        }

        const view =
          event.target.closest(
            '[data-aa-operations-view]'
          );

        if (view) {
          this.syncQuoteItemsFromDom();
          this.activeView =
            view.dataset
              .aaOperationsView ||
            'overview';
          this.render();
          if (this.activeView === 'contracts') this.loadContractData();
          return;
        }

        if (event.target.closest('[data-aa-retry-contracts]')) {
          this.contractDataError = '';
          this.loadContractData();
          return;
        }

        if (
          event.target.closest(
            '[data-aa-new-quote]'
          )
        ) {
          this.quoteBuilderOpen = true;
          this.quoteMessage = '';

          if (
            !this.quoteDraftItems.length
          ) {
            this.quoteDraftItems = [
              this.makeQuoteItem(
                this.quoteServices[0] ||
                null
              )
            ];
          }

          this.render();
          return;
        }

        if (
          event.target.closest(
            '[data-aa-quote-close]'
          )
        ) {
          this.quoteBuilderOpen = false;
          this.quoteMessage = '';
          this.render();
          return;
        }

        if (
          event.target.closest(
            '[data-aa-quote-add-service]'
          )
        ) {
          this.syncQuoteItemsFromDom();
          this.quoteDraftItems.push(
            this.makeQuoteItem(
              this.quoteServices[0] ||
              null
            )
          );
          this.render();
          return;
        }

        const removeService =
          event.target.closest(
            '[data-aa-quote-remove-service]'
          );

        if (removeService) {
          this.syncQuoteItemsFromDom();

          const index =
            Number(
              removeService.dataset
                .aaQuoteRemoveService
            );

          if (
            Number.isInteger(index)
          ) {
            this.quoteDraftItems.splice(
              index,
              1
            );
          }

          this.render();
          return;
        }

        const jump =
          event.target.closest(
            '[data-aa-operations-jump]'
          );

        if (jump) {
          const target =
            this.root.querySelector(
              `[data-aa-operations-section="${jump.dataset.aaOperationsJump}"]`
            );

          target?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          return;
        }

        const toggle =
          event.target.closest(
            '[data-aa-operations-client-toggle]'
          );

        if (!toggle) {
          return;
        }

        const clientId =
          toggle.dataset
            .aaOperationsClientToggle;

        if (
          this.expandedClients.has(
            String(clientId)
          )
        ) {
          this.expandedClients.delete(
            String(clientId)
          );
        } else {
          this.expandedClients.add(
            String(clientId)
          );
        }

        this.render();
      }
    );
  }
}

window.AAOperationsWorkspace =
  AAOperationsWorkspace;
